import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Mic, Play, Square, ChevronRight, ChevronLeft,
  Award, Star, MessageSquare, Plane, Volume2, CheckCircle, X, RefreshCw,
  Headphones, Globe, Shield, Zap, Trophy, Save, MessageCircle, Info, ArrowLeft,
  BookOpen, CheckCircle2, TrendingUp, Activity } from 'lucide-react';
import { cn } from '../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { Progress } from '../components/ui/progress';
import EnglishInterviewPlayer from '../components/EnglishInterviewPlayer';
import { getApiErrorMessage } from '../utils/apiError';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// SpokenLangBadge — STT dilini gosterir + override toggle
// Default scenario.spoken_language; tiklayinca override (langOverride) set eder.
// Reusable: Voice Practice, Interview, Announce ekranlarinda kullanilir.
// ---------------------------------------------------------------------------
function SpokenLangBadge({ defaultLang, override, onToggle, lang, t: _t }) {
  // Effective dil: override varsa o, yoksa defaultLang, yoksa 'en'
  const effective = override || defaultLang || 'en';
  const isOverridden = override != null && override !== defaultLang;

  // WCAG AA: TR mavi (#0369a1 on #e0f2fe = 7.8:1), EN amber (#92400e on #fef3c7 = 6.5:1)
  // Override durumunda kalin border + tooltip
  const colors = effective === 'tr'
    ? 'bg-sky-100 text-sky-800 border-sky-400'
    : 'bg-amber-100 text-amber-800 border-amber-400';

  const labelTr = lang === 'tr' ? 'Konusma Dili' : 'Spoken Language';
  const switchTr = lang === 'tr'
    ? (effective === 'tr' ? "EN'e gec" : "TR'ye gec")
    : (effective === 'tr' ? 'Switch to EN' : 'Switch to TR');

  const handleClick = (e) => {
    e.stopPropagation();
    // Toggle: override null -> diger dil; ayni dil tekrar tiklanirsa null'a don
    const nextOverride = effective === 'tr' ? 'en' : 'tr';
    onToggle(nextOverride === defaultLang ? null : nextOverride);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-all hover:scale-105 ${colors} ${isOverridden ? 'ring-2 ring-offset-1 ring-current' : ''}`}
      aria-label={`${labelTr}: ${effective.toUpperCase()}. ${switchTr}`}
      title={`${labelTr}: ${effective.toUpperCase()}
${switchTr}`}
      data-testid="spoken-lang-badge"
    >
      <Globe size={12} aria-hidden="true" />
      <span>{effective.toUpperCase()}</span>
      {isOverridden && (
        <span className="text-[9px] opacity-70 ml-0.5">
          {lang === 'tr' ? '(ozel)' : '(custom)'}
        </span>
      )}
    </button>
  );
}



// ---------------------------------------------------------------------------
// MediaRecorder helpers — tarayıcı uyumluluğu için MIME kandidat listesi tara
// ---------------------------------------------------------------------------
function pickRecorderMime({ video = false, audio = true } = {}) {
  const candidates = [];
  if (video) {
    candidates.push(
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
      'video/mp4',
    );
  }
  if (audio) {
    candidates.push(
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/mpeg',
    );
  }
  for (const candidate of candidates) {
    try {
      if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(candidate)) {
        return candidate;
      }
    } catch {
      // devam et
    }
  }
  return ''; // boş string → MediaRecorder kendi seçsin
}

/** MIME type'tan dosya uzantısı döndür */
function mimeToExt(mimeType) {
  if (!mimeType) return 'webm';
  if (mimeType.startsWith('audio/mp4') || mimeType.startsWith('video/mp4')) return 'mp4';
  if (mimeType.startsWith('audio/mpeg')) return 'mp3';
  return 'webm';
}

/** Güvenli MediaRecorder oluştur: MIME desteklenmezse options'sız dene */
function createMediaRecorder(stream, mimeType) {
  if (mimeType) {
    try {
      return new MediaRecorder(stream, { mimeType });
    } catch (err) {
      console.warn('[MediaRecorder] MIME başarısız, varsayılana dönülüyor:', mimeType, err);
    }
  }
  return new MediaRecorder(stream);
}
// ---------------------------------------------------------------------------

const MODULES = [
  { id: 'voice_practice', icon: Headphones, label: 'Voice Practice', label_tr: 'İnteraktif Eğitimler', color: '#EC4899', desc_tr: 'Sesli anons ve konuşma pratiği', desc: 'Voice announcement & speaking practice' },
  { id: 'english_interview', icon: Globe, label: 'English Interview', label_tr: 'İngilizce Mülakat', color: '#7C3AED', desc_tr: 'Duolingo tarzı İngilizce mülakat simülasyonu', desc: 'Duolingo-style English interview simulation' },
  { id: 'announcements', icon: Volume2, label: 'Announcements', label_tr: 'Anonslar', color: '#10B981', desc_tr: 'Uçuş anonsları İngilizcesi', desc: 'Flight announcement English' },
  { id: 'passenger_communication', icon: MessageSquare, label: 'Passenger Comm.', label_tr: 'Yolcu İletişimi', color: '#FFC800', desc_tr: 'Yolcu iletişim kalıpları', desc: 'Passenger communication patterns' },
  { id: 'emergency', icon: Shield, label: 'Emergency', label_tr: 'Acil Durum', color: '#FF4B4B', desc_tr: 'Acil durum İngilizcesi', desc: 'Emergency English phrases' },
  { id: 'service', icon: Star, label: 'In-flight Service', label_tr: 'Uçuş Servisi', color: '#CE82FF', desc_tr: 'Servis ve satış kalıpları', desc: 'Service & sales phrases' },
];

export default function CabinCrewPage() {
  const { t, lang } = useLanguage();
  const { authHeaders, user } = useAuth();
  const navigate = useNavigate();
  const [activeModule, setActiveModule] = useState(null);
  const [programEnrollment, setProgramEnrollment] = useState(null);
  const [enrollmentLoaded, setEnrollmentLoaded] = useState(false);
  const [myGroup, setMyGroup] = useState(null);
  const [groupLoaded, setGroupLoaded] = useState(false);

  // Voice practice progress — completed exercise IDs from backend
  const [completedExerciseIds, setCompletedExerciseIds] = useState([]);
  const [loadingVoiceProgress, setLoadingVoiceProgress] = useState(true);

  // Recent activities (Dashboard ActivityFeed reuse)
  const [recentActivities, setRecentActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(true);

  useEffect(() => {
    axios.get(`${API}/cabin-crew/voice-recordings`, { headers: authHeaders() })
      .then(res => {
        const recs = res.data.recordings || [];
        setCompletedExerciseIds(recs.map(r => r.exercise_id));
      })
      .catch(() => {})
      .finally(() => setLoadingVoiceProgress(false));
  }, [authHeaders]);

  useEffect(() => {
    axios.get(`${API}/students/me/activities`, { headers: authHeaders(), params: { limit: 5 } })
      .then(res => setRecentActivities(res.data?.activities || []))
      .catch(() => {})
      .finally(() => setLoadingActivities(false));
  }, [authHeaders]);

  // Modul 3.8 revize: Program kaydi kontrolu (admin/instructor muaf)
  useEffect(() => {
    const role = user?.role;
    if (role === 'admin' || role === 'superadmin' || role === 'instructor') {
      setEnrollmentLoaded(true);
      return;
    }
    axios.get(`${API}/cabin-crew/my-enrollment`, { headers: authHeaders() })
      .then(res => setProgramEnrollment(res.data))
      .catch(() => {})
      .finally(() => setEnrollmentLoaded(true));
  }, [authHeaders, user]);

  // Modul 3.12: Grup bilgisi (admin/instructor muaf)
  useEffect(() => {
    const role = user?.role;
    if (role === 'admin' || role === 'superadmin' || role === 'instructor') {
      setGroupLoaded(true);
      return;
    }
    axios.get(`${API}/my-cabin-crew-group`, { headers: authHeaders() })
      .then(res => setMyGroup(res.data))
      .catch(() => {})
      .finally(() => setGroupLoaded(true));
  }, [authHeaders, user]);

  // Program kaydi yoksa "Kariyer Online ile iletisime gec" banner'i
  if (enrollmentLoaded && programEnrollment && !programEnrollment.has_enrollment) {
    return (
      <div className="min-h-screen bg-[var(--bg-app)] p-4" data-testid="cabin-crew-no-enrollment">
        <div className="max-w-2xl mx-auto bg-blue-50 border-2 border-blue-300 rounded-2xl p-8 mt-20">
          <div className="flex items-center gap-3 mb-4">
            <Plane size={28} className="text-blue-700" />
            <h1 className="font-display text-2xl font-bold text-blue-900">
              {lang === 'tr' ? 'Kabin Memurluğu Programı' : 'Cabin Crew Program'}
            </h1>
          </div>
          <p className="text-sm mb-6 text-blue-900">
            {lang === 'tr'
              ? 'Kabin Memurluğu programına henüz kayıtlı değilsiniz. Kariyer Online ile iletişime geçerek kaydınızı yapabilirsiniz.'
              : 'You are not yet enrolled in the Cabin Crew program. Contact Kariyer Online to enroll.'}
          </p>
          <div className="bg-white/70 rounded-xl p-4 mb-4 text-sm">
            <p className="font-bold mb-2">{lang === 'tr' ? 'İletişim' : 'Contact'}:</p>
            <p>Phone: +90 XXX XXX XX XX</p>
            <p>WhatsApp: +90 XXX XXX XX XX</p>
            <p>Email: info@kariyeronline.com.tr</p>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 rounded-xl border-2 border-blue-300 text-blue-900 font-bold hover:bg-blue-100"
          >
            {lang === 'tr' ? 'Panele Don' : 'Back to Dashboard'}
          </button>
        </div>
      </div>
    );
  }

  // Reviewer fix turu (2026-09-29): web seviye tespit sinavi kaldirildi ama
  // Voice Practice/Aviation English UC'lari seviye kontrolu yapmiyor ve
  // English Interview "Basla"da 403 level_required donuyor — kayitli ama
  // henuz seviyesi atanmamis (cabin_crew_level yok) bir ogrenciyi eskiden
  // placement gate durduruyordu, simdi bu notr ekran durduruyor. Admin/
  // instructor muaf (enrollment/grup kontrolleriyle ayni desen).
  const levelGateExempt = user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'instructor';
  if (!levelGateExempt && !user?.cabin_crew_level) {
    return (
      <div className="min-h-screen bg-[var(--bg-app)] p-4" data-testid="cabin-crew-level-gate">
        <div className="max-w-2xl mx-auto bg-slate-50 border-2 border-slate-300 rounded-2xl p-8 mt-20">
          <div className="flex items-center gap-3 mb-4">
            <Plane size={28} className="text-slate-700" />
            <h1 className="font-display text-2xl font-bold text-slate-900">
              {t('cabinCrew.levelGate.title')}
            </h1>
          </div>
          <p className="text-sm mb-6 text-slate-900">
            {t('cabinCrew.levelGate.desc')}
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 rounded-xl border-2 border-slate-300 text-slate-900 font-bold hover:bg-slate-100"
          >
            {lang === 'tr' ? 'Panele Don' : 'Back to Dashboard'}
          </button>
        </div>
      </div>
    );
  }

  if (activeModule === 'voice_practice') {
    return (
      <VoicePracticeModule
        onBack={() => setActiveModule(null)}
        onExerciseSaved={(exerciseId) => {
          setCompletedExerciseIds(prev =>
            prev.includes(exerciseId) ? prev : [...prev, exerciseId]
          );
        }}
      />
    );
  }
  if (activeModule === 'english_interview') {
    return <EnglishInterviewModule onBack={() => setActiveModule(null)} />;
  }
  if (['announcements', 'passenger_communication', 'emergency', 'service'].includes(activeModule)) {
    return <AviationEnglishModule module={activeModule} onBack={() => setActiveModule(null)} />;
  }

  // Compute voice practice progress from backend-fetched IDs
  const totalExerciseCount = VOICE_EXERCISES.reduce((sum, cat) => sum + cat.exercises.length, 0);
  const totalCompleted = completedExerciseIds.length;
  const completedCategories = VOICE_EXERCISES.filter(
    cat => cat.exercises.every(ex => completedExerciseIds.includes(ex.id))
  ).length;

  return (
    <div className="min-h-screen bg-[var(--bg-app)]" data-testid="cabin-crew-page">
      {/* Hero */}
      <div className="bg-gradient-to-br from-sky-600 to-blue-800 text-white py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-2">
            <Plane size={28} />
            <h1 className="font-display text-3xl font-bold">
              {lang === 'tr' ? 'Kabin Memurluğu Eğitimi' : 'Cabin Crew Training'}
            </h1>
          </div>
          <p className="text-sky-200 font-medium max-w-xl">
            {lang === 'tr'
              ? 'AI destekli mülakat pratiği, havacılık İngilizcesi ve interaktif modüllerle kabin memurluğuna hazırlanın.'
              : 'Prepare for cabin crew with AI-powered interview practice, aviation English, and interactive modules.'}
          </p>
          {/* Stats — voice practice progress */}
          <div className="flex gap-4 mt-6 flex-wrap">
            <div className="bg-white/15 backdrop-blur-sm rounded-xl px-4 py-2">
              <p className="text-2xl font-bold">{loadingVoiceProgress ? '—' : totalCompleted}</p>
              <p className="text-[10px] font-bold text-sky-200">{lang === 'tr' ? 'Egzersiz Tamamlandı' : 'Exercises Done'}</p>
            </div>
            <div className="bg-white/15 backdrop-blur-sm rounded-xl px-4 py-2">
              <p className="text-2xl font-bold">{loadingVoiceProgress ? '—' : `${completedCategories}/${VOICE_EXERCISES.length}`}</p>
              <p className="text-[10px] font-bold text-sky-200">{lang === 'tr' ? 'Kategori Bitirildi' : 'Categories Done'}</p>
            </div>
            {totalExerciseCount > 0 && !loadingVoiceProgress && (
              <div className="bg-white/15 backdrop-blur-sm rounded-xl px-4 py-2">
                <p className="text-2xl font-bold">{Math.round((totalCompleted / totalExerciseCount) * 100)}%</p>
                <p className="text-[10px] font-bold text-sky-200">{lang === 'tr' ? 'Genel İlerleme' : 'Overall Progress'}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Modul 3.12: Grubum karti veya "henuz atanmadiniz" info */}
        {groupLoaded && myGroup && myGroup.has_group && (
          <div
            className="bg-green-50 border-2 border-green-200 rounded-2xl p-4 mb-6"
            data-testid="my-group-card"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                  <MessageCircle size={20} className="text-green-700" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-bold text-sm truncate">
                    {lang === 'tr' ? myGroup.group.name : (myGroup.group.name_en || myGroup.group.name)}
                  </h3>
                  <p className="text-[11px] text-[var(--text-muted)]">
                    {myGroup.group.current_count}/{myGroup.group.capacity} {lang === 'tr' ? 'uye' : 'members'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate('/my-cabin-crew-group')}
                className="px-3 py-1.5 text-sm font-bold bg-green-600 text-white rounded-lg hover:bg-green-700 flex-shrink-0"
                data-testid="go-to-my-group"
              >
                {lang === 'tr' ? 'Gruba Git' : 'Go to Group'}
              </button>
            </div>
          </div>
        )}

        {groupLoaded && myGroup && !myGroup.has_group && myGroup.reason === 'no_group_assigned' && (
          <div
            className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-6 text-sm flex items-center gap-2"
            data-testid="my-group-pending"
          >
            <Info size={16} className="text-blue-600 flex-shrink-0" />
            <p>
              {lang === 'tr'
                ? 'Grubunuz yakinda atanacak. Admin seviyenize uygun bir grup belirleyecek.'
                : 'Your group will be assigned soon. Admin will set a group matching your level.'}
            </p>
          </div>
        )}

        {/* Module Grid */}
        <h2 className="font-display text-xl font-bold mb-4">{lang === 'tr' ? 'Eğitim Modülleri' : 'Training Modules'}</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-10">
          {MODULES.map((m, i) => {
            const Icon = m.icon;
            return (
              <motion.button
                key={m.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                onClick={() => setActiveModule(m.id)}
                className="card-clay p-5 text-left hover:border-[var(--brand-primary)] transition-all group"
                data-testid={`module-${m.id}`}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: m.color + '20' }}>
                  <Icon size={24} style={{ color: m.color }} />
                </div>
                <h3 className="font-bold text-sm mb-1">{lang === 'tr' ? m.label_tr : m.label}</h3>
                <p className="text-[11px] text-[var(--text-muted)] font-medium">{lang === 'tr' ? m.desc_tr : m.desc}</p>
                <ChevronRight size={16} className="text-[var(--text-muted)] mt-2 group-hover:text-[var(--brand-primary)] transition-colors" />
              </motion.button>
            );
          })}
        </div>

        {/* Progress overview + Recent Activities (replaces broken "Geçmiş Mülakatlar") */}
        <CabinCrewProgressSection
          lang={lang}
          completedExerciseIds={completedExerciseIds}
          loadingVoiceProgress={loadingVoiceProgress}
          recentActivities={recentActivities}
          loadingActivities={loadingActivities}
        />
      </div>
    </div>
  );
}

// ============ CABIN CREW PROGRESS SECTION ============
// Replaces the old "Geçmiş Mülakatlar" section that showed broken %0 / "devam ediyor" data.
// Shows: voice practice progress per category + recent platform activities.

const ACTIVITY_ICONS_CC = {
  lesson_complete:    { icon: BookOpen,      color: 'text-cyan-600',    bg: 'bg-cyan-50' },
  quiz_passed:        { icon: CheckCircle2,  color: 'text-emerald-600', bg: 'bg-emerald-50' },
  exam_passed:        { icon: CheckCircle2,  color: 'text-emerald-600', bg: 'bg-emerald-50' },
  certificate_earned: { icon: Award,         color: 'text-amber-600',   bg: 'bg-amber-50' },
  voice_practice:     { icon: Headphones,    color: 'text-pink-600',    bg: 'bg-pink-50' },
  default:            { icon: Activity,      color: 'text-gray-500',    bg: 'bg-gray-50' },
};

function formatActivityDate(iso, lang) {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat(lang === 'tr' ? 'tr-TR' : 'en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return '';
  }
}

function CabinCrewProgressSection({ lang, completedExerciseIds, loadingVoiceProgress, recentActivities, loadingActivities }) {
  // Per-category progress
  const categoryProgress = VOICE_EXERCISES.map(cat => {
    const done = cat.exercises.filter(ex => completedExerciseIds.includes(ex.id)).length;
    const total = cat.exercises.length;
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
    const complete = done === total && total > 0;
    return { ...cat, done, total, pct, complete };
  });

  const anyVoiceProgress = completedExerciseIds.length > 0;
  const hasActivities = recentActivities.length > 0;

  if (!anyVoiceProgress && !hasActivities && !loadingVoiceProgress && !loadingActivities) {
    return null; // nothing to show, keep page clean
  }

  return (
    <div className="mt-2 space-y-8" data-testid="cabin-crew-progress-section">
      {/* Voice Practice Category Progress */}
      {(loadingVoiceProgress || anyVoiceProgress) && (
        <section data-testid="vp-category-progress">
          <h2 className="font-display text-xl font-bold mb-4 flex items-center gap-2">
            <TrendingUp size={20} className="text-pink-500" />
            {lang === 'tr' ? 'İnteraktif Eğitim İlerlemesi' : 'Interactive Training Progress'}
          </h2>
          {loadingVoiceProgress ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="card-clay p-4 animate-pulse">
                  <div className="h-4 bg-gray-100 rounded mb-2 w-2/3" />
                  <div className="h-2 bg-gray-100 rounded w-full" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {categoryProgress.map(cat => (
                <motion.div
                  key={cat.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`card-clay p-4 ${cat.complete ? 'border-green-300 bg-green-50/50' : ''}`}
                  data-testid={`vp-progress-${cat.id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xl">{cat.icon}</span>
                    {cat.complete && <CheckCircle size={16} className="text-green-600" />}
                  </div>
                  <p className="font-bold text-xs mb-1 truncate">{lang === 'tr' ? cat.category : cat.category_en}</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${cat.complete ? 'bg-green-500' : 'bg-pink-400'}`}
                        style={{ width: `${cat.pct}%` }}
                      />
                    </div>
                    <span className={`text-[9px] font-bold ${cat.complete ? 'text-green-600' : 'text-[var(--text-muted)]'}`}>
                      {cat.done}/{cat.total}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Recent Activities */}
      {(loadingActivities || hasActivities) && (
        <section data-testid="cabin-crew-recent-activities">
          <h2 className="font-display text-xl font-bold mb-4 flex items-center gap-2">
            <Activity size={20} className="text-sky-500" />
            {lang === 'tr' ? 'Son Aktiviteler' : 'Recent Activities'}
          </h2>
          {loadingActivities ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="card-clay p-4 flex items-center gap-3 animate-pulse">
                  <div className="w-10 h-10 rounded-xl bg-gray-100 flex-shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 bg-gray-100 rounded w-3/4" />
                    <div className="h-2.5 bg-gray-100 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {recentActivities.map((act, i) => {
                const iconDef = ACTIVITY_ICONS_CC[act.type] || ACTIVITY_ICONS_CC.default;
                const Icon = iconDef.icon;
                return (
                  <motion.div
                    key={act.id || i}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="card-clay p-4 flex items-center gap-3"
                    data-testid={`cc-activity-${i}`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${iconDef.bg}`}>
                      <Icon size={18} className={iconDef.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm truncate">{act.title}</p>
                      {act.course_name && (
                        <p className="text-[11px] text-[var(--text-muted)] truncate">{act.course_name}</p>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      {(act.xp > 0) && (
                        <span className="block text-[11px] font-bold text-amber-600">+{act.xp} XP</span>
                      )}
                      <span className="block text-[10px] text-[var(--text-muted)]">
                        {formatActivityDate(act.timestamp || act.created_at, lang)}
                      </span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </section>
      )}
    </div>
  );
}


// ============ AVIATION ENGLISH MODULE ============

// Mapping: which modules have admin static content endpoints
const STATIC_CONTENT_ENDPOINTS = {
  announcements: '/announce-templates',
  passenger_communication: '/pax-comm/situations',
  // emergency and inflight_service: no admin endpoint → AI only
};

// Kategori renk paleti — badge için
const CATEGORY_COLORS = {
  welcome:   'bg-sky-100 text-sky-700',
  safety:    'bg-red-100 text-red-700',
  emergency: 'bg-orange-100 text-orange-700',
  weather:   'bg-amber-100 text-amber-700',
  landing:   'bg-emerald-100 text-emerald-700',
  service:   'bg-purple-100 text-purple-700',
  greeting:  'bg-pink-100 text-pink-700',
  request:   'bg-indigo-100 text-indigo-700',
  default:   'bg-gray-100 text-gray-700',
};

// ---------------------------------------------------------------------------
// Slot yardımcı: template metni parçalara böler — metin ve slot kısımları
// ---------------------------------------------------------------------------
function parseTemplateSlots(text) {
  // "{key}" ifadelerini ayır; her parça ya {type:'text',value} ya {type:'slot',key}
  const parts = [];
  const regex = /\{([a-z_]+)\}/gi;
  let last = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) {
      parts.push({ type: 'text', value: text.slice(last, match.index) });
    }
    parts.push({ type: 'slot', key: match[1] });
    last = match.index + match[0].length;
  }
  if (last < text.length) {
    parts.push({ type: 'text', value: text.slice(last) });
  }
  return parts;
}

/** Slot değerlerini template metne uygula */
function fillTemplate(text, slotValues) {
  if (!text) return text;
  return text.replace(/\{([a-z_]+)\}/gi, (_, key) => slotValues[key] ?? `[${key}]`);
}

// Template card for announce templates
function AnnounceTemplateCard({ item, lang, t, getField }) {
  const [expanded, setExpanded] = useState(false);
  const [practiceOpen, setPracticeOpen] = useState(false);
  const [slotValues, setSlotValues] = useState({});
  const title = getField(item, 'title');
  const templateText = getField(item, 'template');
  const category = item.category || '';

  // Kart açıldığında slot değerlerini example ile başlat
  useEffect(() => {
    if (expanded && item.slots && item.slots.length > 0) {
      const initial = {};
      item.slots.forEach(s => {
        const exampleKey = lang === 'tr' ? 'example_tr' : 'example_en';
        initial[s.key] = s[exampleKey] || s.example_tr || s.example_en || '';
      });
      setSlotValues(initial);
    }
  }, [expanded, item, lang]);

  // Doldurulmuş metin — Dinle ve Pratik Yap bunu kullanır
  const filledText = item.slots && item.slots.length > 0
    ? fillTemplate(templateText, slotValues)
    : templateText;

  // Template parçaları — inline slot input render için
  const templateParts = templateText ? parseTemplateSlots(templateText) : [];
  const hasSlots = item.slots && item.slots.length > 0;

  // slot meta: key → { label, example }
  const slotMeta = {};
  if (item.slots) {
    item.slots.forEach(s => {
      const labelKey = lang === 'tr' ? 'label_tr' : 'label_en';
      const exampleKey = lang === 'tr' ? 'example_tr' : 'example_en';
      slotMeta[s.key] = {
        label: s[labelKey] || s.label_en || s.label_tr || s.key,
        example: s[exampleKey] || s.example_en || s.example_tr || '',
      };
    });
  }

  const updateSlot = (key, value) => setSlotValues(prev => ({ ...prev, [key]: value }));

  // Stop event propagation for inner buttons so card toggle doesn't fire
  const stopProp = (e) => e.stopPropagation();

  return (
    <article
      className="card-clay cursor-pointer border border-gray-100 rounded-2xl hover:shadow-md hover:scale-[1.01] transition-all duration-200 flex flex-col h-full w-full"
      onClick={() => { if (!practiceOpen) setExpanded(prev => !prev); }}
      aria-expanded={expanded}
      aria-label={title}
    >
      {/* Header — always visible */}
      <div className="flex items-start justify-between gap-2 p-4 pb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={cn(
              'text-[10px] font-bold px-2 py-0.5 rounded-lg capitalize',
              CATEGORY_COLORS[category?.toLowerCase()] || CATEGORY_COLORS.default
            )}>
              {category}
            </span>
          </div>
          <h3 className="font-display font-bold text-base leading-snug">{title}</h3>
        </div>
        <ChevronRight
          size={18}
          className={`text-[var(--text-muted)] flex-shrink-0 transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}
        />
      </div>

      {expanded && templateText && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="flex-1 flex flex-col px-4 pb-4 pt-3 border-t border-[var(--border-light)]"
          onClick={stopProp}
        >
          {/* İçerik alanı — flex-1 ile alta kalan boşluğu doldurur */}
          <div className="flex-1">
            {/* Slot doldurma başlığı — sadece slot varsa göster */}
            {hasSlots && (
              <p className="text-[11px] font-bold text-amber-600 mb-2 uppercase tracking-wide flex items-center gap-1">
                <Info size={12} />
                {t('cabinCrew.announce.fillSlots')}
              </p>
            )}

            {/* İŞ 1: Inline slot input'lu template görünümü */}
            {/* font-sans (Nunito/system) — Türkçe diakritik uyumlu, Fredoka değil */}
            <div className="font-sans text-sm leading-relaxed text-[var(--text-body)] font-medium bg-sky-50 rounded-xl p-3 border border-sky-200">
              {hasSlots ? (
                <span>
                  {templateParts.map((part, i) => {
                    if (part.type === 'text') {
                      return <span key={i}>{part.value}</span>;
                    }
                    const meta = slotMeta[part.key] || { label: part.key, example: '' };
                    return (
                      <input
                        key={i}
                        type="text"
                        value={slotValues[part.key] ?? ''}
                        onChange={e => updateSlot(part.key, e.target.value)}
                        placeholder={meta.example || meta.label}
                        aria-label={meta.label}
                        onClick={e => e.stopPropagation()}
                        className="inline-block min-w-[72px] max-w-[160px] px-2 py-0.5 mx-0.5 bg-amber-50 border-0 border-b-2 border-amber-400 text-amber-900 font-bold focus:outline-none focus:border-amber-600 rounded-sm text-sm transition-colors"
                        style={{ width: `${Math.max(72, (slotValues[part.key] || meta.example || meta.label || part.key).length * 9 + 16)}px` }}
                      />
                    );
                  })}
                </span>
              ) : (
                <span>{templateText}</span>
              )}
            </div>

            {/* Slot label listesi — yardımcı bilgi */}
            {hasSlots && (
              <div className="mt-2 flex flex-wrap gap-1">
                {item.slots.map((slot, si) => {
                  const meta = slotMeta[slot.key] || { label: slot.key, example: '' };
                  return (
                    <span key={si} className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-lg font-bold">
                      {meta.label}
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer butonlar — içerik alanının altına yapışır */}
          {!practiceOpen && (
            <div className="mt-4 flex gap-2">
              <AnnounceTtsButton text={filledText} currentLocale={lang} t={t} />
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={(e) => { e.stopPropagation(); setPracticeOpen(true); }}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-500 text-white text-xs font-bold border-2 border-emerald-600 shadow-[2px_2px_0_#059669] hover:shadow-[1px_1px_0_#059669] transition-all"
                data-testid="announce-practice-btn"
              >
                <Mic size={14} />
                {t('cabinCrew.announce.practice')}
              </motion.button>
            </div>
          )}

          {practiceOpen && (
            <AnnouncePracticeView
              templateText={filledText}
              templateId={item.id || item._id}
              templateLang={getField(item, 'spoken_language') || 'en'}
              lang={lang}
              t={t}
              onClose={() => setPracticeOpen(false)}
            />
          )}
        </motion.div>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------
// TTS yardımcı fonksiyonlar — dil tespiti + voice seçimi
// ---------------------------------------------------------------------------

/** Metni ve kullanıcı locale'ini analiz ederek dil kodu döndürür ('tr' | 'en') */
function detectTtsLang(text, currentLocale) {
  if (!text) return currentLocale === 'tr' ? 'tr' : 'en';
  // Türkçe'ye özgü diakritik karakter varsa Türkçe say
  if (/[şğıçöüİĞŞÇÖÜ]/.test(text)) return 'tr';
  // Diakritik yoksa kullanıcı locale'ini kullan (admin diakritiksiz yazmış olabilir)
  if (currentLocale === 'tr') return 'tr';
  return 'en';
}

/** Tarayıcıdan uygun voice'u seç; bulunamazsa null döner */
function pickTtsVoice(lang) {
  if (typeof window === 'undefined' || !window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices();
  const prefix = lang === 'tr' ? 'tr' : 'en';
  // Önce Female sesli arama, sonra herhangi biri
  return (
    voices.find(v => v.lang.toLowerCase().startsWith(prefix) && v.name.toLowerCase().includes('female')) ||
    voices.find(v => v.lang.toLowerCase().startsWith(prefix)) ||
    null
  );
}

/** SpeechSynthesisUtterance oluştur, dile göre ayarla ve çal */
function playTts(text, lang, onEnd, onError) {
  if (typeof window === 'undefined' || !window.speechSynthesis) return null;
  window.speechSynthesis.cancel(); // önceki konuşmayı durdur
  const utt = new SpeechSynthesisUtterance(text);
  utt.lang = lang === 'tr' ? 'tr-TR' : 'en-US';
  utt.rate = lang === 'tr' ? 0.95 : 0.85;
  utt.pitch = 1;
  // Voices listesi bazen asenkron yüklenir; tekrar dene
  const trySetVoice = () => {
    const voice = pickTtsVoice(lang);
    if (voice) utt.voice = voice;
  };
  trySetVoice();
  if (onEnd) utt.onend = onEnd;
  if (onError) utt.onerror = onError;
  window.speechSynthesis.speak(utt);
  return utt;
}

// ---------------------------------------------------------------------------

// TTS butonu — ayrı component, state izole
function AnnounceTtsButton({ text, currentLocale, t }) {
  const [playing, setPlaying] = useState(false);
  const [noVoiceWarning, setNoVoiceWarning] = useState(false);

  const handleListen = (e) => {
    e.stopPropagation();
    if (playing) {
      window.speechSynthesis.cancel();
      setPlaying(false);
      return;
    }
    const lang = detectTtsLang(text, currentLocale);
    // Türkçe isteniyor ama voice yoksa uyar
    if (lang === 'tr' && !pickTtsVoice('tr')) {
      setNoVoiceWarning(true);
      setTimeout(() => setNoVoiceWarning(false), 4000);
    }
    setPlaying(true);
    playTts(
      text,
      lang,
      () => setPlaying(false),
      () => setPlaying(false),
    );
  };

  return (
    <div className="flex flex-col gap-1">
      <motion.button
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        onClick={handleListen}
        className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold border-2 shadow-[2px_2px_0_#0284c7] hover:shadow-[1px_1px_0_#0284c7] transition-all ${
          playing
            ? 'bg-sky-600 text-white border-sky-700'
            : 'bg-sky-50 text-sky-700 border-sky-300'
        }`}
        data-testid="announce-listen-btn"
      >
        {playing ? <Square size={12} fill="currentColor" /> : <Volume2 size={14} />}
        {playing ? t('cabinCrew.announce.stopListen') : t('cabinCrew.announce.listen')}
      </motion.button>
      {noVoiceWarning && (
        <p className="text-[10px] text-amber-600 font-semibold leading-tight max-w-[180px]">
          {t('cabinCrew.announce.noTrVoice')}
        </p>
      )}
    </div>
  );
}

// Pratik akışı component'i
function AnnouncePracticeView({ templateText, templateId: _templateId, templateLang, lang, t, onClose }) {
  const { authHeaders } = useAuth();
  const toast = useToast();

  // phase: idle | recording | processing | result
  const [phase, setPhase] = useState('idle');
  const [recording, setRecording] = useState(false);
  const [_audioBlob, setAudioBlob] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  // Override badge: STT dilini kullanici degistirebilir. null = templateLang fallback.
  const [langOverride, setLangOverride] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const stopProp = (e) => e.stopPropagation();

  // Kronometre
  useEffect(() => {
    if (recording) {
      timerRef.current = setInterval(() => setElapsed(p => p + 1), 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [recording]);

  const formatTime = (sec) => `${String(Math.floor(sec / 60)).padStart(2, '0')}:${String(sec % 60).padStart(2, '0')}`;

  const startRecording = async (e) => {
    e.stopPropagation();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];
      // Tarayıcı uyumlu MIME seç (Chrome/Firefox/Safari)
      const mimeType = pickRecorderMime({ audio: true });
      const mr = createMediaRecorder(stream, mimeType);
      mr.ondataavailable = (ev) => { if (ev.data.size > 0) chunksRef.current.push(ev.data); };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      setElapsed(0);
      setAudioBlob(null);
      setFeedback(null);
      setPhase('recording');
    } catch {
      toast.error(t('cabinCrew.announce.micError'));
    }
  };

  const stopRecording = (e) => {
    e.stopPropagation();
    if (!mediaRecorderRef.current) return;
    mediaRecorderRef.current.onstop = () => {
      const mimeType = mediaRecorderRef.current.mimeType || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type: mimeType });
      setAudioBlob(blob);
      mediaRecorderRef.current.stream.getTracks().forEach(tr => tr.stop());
      evaluateBlob(blob);
    };
    mediaRecorderRef.current.stop();
    setRecording(false);
    setPhase('processing');
  };

  const evaluateBlob = async (blob) => {
    const ext = mimeToExt(blob.type);
    try {
      // 1) Transcribe — dil onceligi: override > templateLang > 'en'
      const fd = new FormData();
      fd.append('file', blob, `announce.${ext}`);
      const sttLang = langOverride || templateLang || 'en';
      fd.append('language', sttLang);
      const transRes = await axios.post(`${API}/cabin-crew/interview/transcribe`, fd, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      const transcribed = transRes.data.text || '';

      // 2) Evaluate — voice-practice endpoint (category: 'announce')
      // NOTE: backend uses 'announce' not 'announcements'; wrong category → 'general' fallback
      let evalData;
      try {
        // NOTE: exercise_id / scenario_id intentionally omitted for announce template practice.
        // Backend /voice-practice/evaluate treats any non-empty scenario_id as a DB lookup
        // against voice_practice_scenarios; announce template IDs live in announce_templates
        // and would cause scenario_not_found (404). Omitting the field lets the backend
        // skip the lookup and use category + reference_text for rubric selection only.
        const evalRes = await axios.post(`${API}/cabin-crew/voice-practice/evaluate`, {
          reference_text: templateText,
          spoken_text: transcribed,
          category: 'announce'
        }, { headers: authHeaders() });
        // Backend returns: { scores: { phrase_accuracy, tone_politeness, icao_clarity, pace, overall },
        //   feedback_tr, feedback_en, improvements_tr, improvements_en, ... }
        // Map to flat shape expected by score grid: pronunciation/clarity/pace/accuracy/overall
        const raw = evalRes.data;
        const sc = raw.scores || {};
        evalData = {
          // Map announce rubric → display keys
          pronunciation: sc.icao_clarity   ?? sc.pronunciation   ?? 0,
          clarity:       sc.phrase_accuracy ?? sc.clarity         ?? 0,
          pace:          sc.pace            ?? 0,
          accuracy:      sc.tone_politeness ?? sc.accuracy        ?? 0,
          overall:       sc.overall         ?? 0,
          feedback: lang === 'tr' ? (raw.feedback_tr || '') : (raw.feedback_en || ''),
          tips: lang === 'tr' ? (raw.improvements_tr || []) : (raw.improvements_en || []),
          // Keep full raw for advanced consumers
          _raw: raw,
        };
      } catch (err) {
        // 502 (ai_unavailable) or network error — show error toast, do not show 0 scores
        const detail = err?.response?.data;
        const errMsg = detail
          ? (lang === 'tr' ? (detail.message_tr || detail.message_en) : (detail.message_en || detail.message_tr))
          : (lang === 'tr' ? 'Değerlendirme sunucuya ulaşamadı. Bağlantınızı kontrol edin.' : 'Evaluation server unreachable. Check your connection.');
        toast.error(errMsg);
        setPhase('idle');
        return;
      }
      setFeedback({ ...evalData, transcribed_text: transcribed });
      setPhase('result');
    } catch (err) {
      console.error('Announce practice eval error:', err);
      toast.error(t('cabinCrew.announce.evalError'));
      setPhase('idle');
    }
  };

  const reset = (e) => {
    e.stopPropagation();
    setPhase('idle');
    setFeedback(null);
    setAudioBlob(null);
    setElapsed(0);
  };

  const scoreColor = (s) => (s >= 85 ? 'text-green-600' : s >= 70 ? 'text-yellow-600' : 'text-red-500');
  const scoreBg = (s) => (s >= 85 ? 'bg-green-50 border-green-200' : s >= 70 ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200');

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-4 rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-4 space-y-4"
      onClick={stopProp}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold text-emerald-700 uppercase tracking-wide flex items-center gap-1">
          <Mic size={14} /> {t('cabinCrew.announce.practiceTitle')}
        </p>
        <button
          onClick={onClose}
          className="text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors"
          aria-label="Kapat"
        >
          <X size={16} />
        </button>
      </div>

      {/* Konusma dili badge — override toggle (TR/EN). Default: templateLang */}
      {(phase === 'idle' || phase === 'recording') && (
        <div className="flex justify-center mb-2" onClick={stopProp}>
          <SpokenLangBadge
            defaultLang={templateLang || 'en'}
            override={langOverride}
            onToggle={setLangOverride}
            lang={lang}
            t={t}
          />
        </div>
      )}

      {/* IDLE */}
      {phase === 'idle' && (
        <div className="text-center py-2">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={startRecording}
            className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 text-white flex items-center justify-center mx-auto shadow-[4px_4px_0_#059669] hover:shadow-[2px_2px_0_#059669] transition-all"
            data-testid="announce-start-record-btn"
          >
            <Mic size={30} />
          </motion.button>
          <p className="text-xs font-semibold text-[var(--text-muted)] mt-3">{t('cabinCrew.announce.startRecord')}</p>
        </div>
      )}

      {/* RECORDING */}
      {phase === 'recording' && (
        <div className="text-center py-2">
          <motion.button
            animate={{ scale: [1, 1.08, 1] }}
            transition={{ repeat: Infinity, duration: 0.9 }}
            onClick={stopRecording}
            className="w-20 h-20 rounded-full bg-red-600 text-white flex items-center justify-center mx-auto shadow-[4px_4px_0_#b91c1c]"
            data-testid="announce-stop-record-btn"
          >
            <Square size={24} fill="white" />
          </motion.button>
          <p className="text-sm font-bold text-red-600 mt-3">{t('cabinCrew.announce.recording')}</p>
          <p className="text-xs font-mono text-[var(--text-muted)] mt-1">{formatTime(elapsed)}</p>
          <p className="text-[10px] text-[var(--text-muted)] mt-1">{t('cabinCrew.announce.stopRecord')}</p>
        </div>
      )}

      {/* PROCESSING */}
      {phase === 'processing' && (
        <div className="text-center py-6">
          <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm font-bold text-emerald-700">{t('cabinCrew.announce.evaluating')}</p>
        </div>
      )}

      {/* RESULT */}
      {phase === 'result' && feedback && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
          {/* Transcript */}
          {feedback.transcribed_text && (
            <div className="bg-white rounded-xl border border-emerald-200 p-3">
              <p className="text-[10px] font-bold text-[var(--text-muted)] mb-1 uppercase tracking-wide">
                {t('cabinCrew.announce.transcript')}
              </p>
              <p className="font-sans text-sm font-medium italic text-[var(--text-body)]">
                "{feedback.transcribed_text}"
              </p>
            </div>
          )}

          {/* Score grid */}
          <div className="grid grid-cols-5 gap-2 text-center">
            {[
              { key: 'pronunciation', label: t('cabinCrew.announce.pronunciationLabel') },
              { key: 'clarity',       label: t('cabinCrew.announce.clarityLabel') },
              { key: 'pace',          label: t('cabinCrew.announce.paceLabel') },
              { key: 'accuracy',      label: t('cabinCrew.announce.accuracyLabel') },
              { key: 'overall',       label: t('cabinCrew.announce.overallLabel') },
            ].map(s => (
              <div key={s.key} className={`rounded-xl border p-2 ${scoreBg(feedback[s.key] || 0)}`}>
                <p className={`font-display text-xl font-bold ${scoreColor(feedback[s.key] || 0)}`}>
                  {feedback[s.key] || 0}
                </p>
                <p className="text-[8px] font-bold text-[var(--text-muted)] leading-tight mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>

          {/* AI Feedback text */}
          {feedback.feedback && (
            <div className="bg-sky-50 border border-sky-200 rounded-xl p-3">
              <p className="text-[10px] font-bold text-sky-700 mb-1">{t('cabinCrew.announce.aiFeedback')}</p>
              <p className="font-sans text-sm text-sky-800">{feedback.feedback}</p>
            </div>
          )}

          {/* Tips */}
          {feedback.tips && feedback.tips.length > 0 && (
            <div className="space-y-1">
              {feedback.tips.map((tip, ti) => (
                <p key={ti} className="text-xs flex items-start gap-1 text-[var(--text-body)]">
                  <ChevronRight size={12} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                  {tip}
                </p>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-1">
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={reset}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl border-2 border-emerald-300 bg-white text-emerald-700 text-xs font-bold shadow-[2px_2px_0_#6ee7b7] hover:shadow-[1px_1px_0_#6ee7b7] transition-all"
              data-testid="announce-try-again-btn"
            >
              <RefreshCw size={13} />
              {t('cabinCrew.announce.tryAgain')}
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={onClose}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl border-2 border-gray-200 bg-white text-[var(--text-muted)] text-xs font-bold transition-all"
            >
              <X size={13} />
              {lang === 'tr' ? 'Kapat' : 'Close'}
            </motion.button>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}

// Scenario card for pax-comm situations
function PaxCommSituationCard({ item, lang: _lang, t, getField }) {
  const [expanded, setExpanded] = useState(false);
  const title = getField(item, 'title');
  const context = getField(item, 'context');
  const tips = getField(item, 'tips');
  const category = item.category || '';

  return (
    <article
      className="card-clay cursor-pointer border border-gray-100 rounded-2xl hover:shadow-md hover:scale-[1.01] transition-all duration-200 flex flex-col h-full w-full"
      onClick={() => setExpanded(prev => !prev)}
      aria-expanded={expanded}
      aria-label={title}
    >
      {/* Header — always visible */}
      <div className="flex items-start justify-between gap-2 p-4 pb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={cn(
              'text-[10px] font-bold px-2 py-0.5 rounded-lg capitalize',
              CATEGORY_COLORS[category?.toLowerCase()] || CATEGORY_COLORS.default
            )}>
              {category}
            </span>
          </div>
          <h3 className="font-display font-bold text-base leading-snug">{title}</h3>
        </div>
        <ChevronRight
          size={18}
          className={`text-[var(--text-muted)] flex-shrink-0 transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}
        />
      </div>

      {expanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="flex-1 px-4 pb-4 pt-3 border-t border-[var(--border-light)] space-y-3"
        >
          {context && (
            <div>
              <p className="text-[11px] font-bold text-[var(--text-muted)] mb-1 uppercase tracking-wide">
                {t('cabinCrew.aviationEnglish.contextLabel')}
              </p>
              <p className="text-sm text-[var(--text-body)] font-medium bg-amber-50 rounded-xl p-3 border border-amber-200">
                {context}
              </p>
            </div>
          )}
          {item.responses && item.responses.length > 0 && (
            <div>
              <p className="text-[11px] font-bold text-[var(--text-muted)] mb-1 uppercase tracking-wide">
                {t('cabinCrew.aviationEnglish.responsesLabel')}
              </p>
              <div className="space-y-1">
                {item.responses.map((r, ri) => (
                  <div key={ri} className="flex items-start gap-2 text-sm">
                    <span className="text-[10px] font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-md mt-0.5 flex-shrink-0 capitalize">
                      {r.tone}
                    </span>
                    <p className="text-[var(--text-body)]">{getField(r, 'response')}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {tips && (
            <div>
              <p className="text-[11px] font-bold text-[var(--text-muted)] mb-1 uppercase tracking-wide">
                {t('cabinCrew.aviationEnglish.tipsLabel')}
              </p>
              <p className="text-sm text-[var(--text-body)] italic">{tips}</p>
            </div>
          )}
        </motion.div>
      )}
    </article>
  );
}

function AviationEnglishModule({ module, onBack }) {
  const { lang, t, getField } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();

  // Static content state
  const [staticItems, setStaticItems] = useState([]);
  const [staticLoading, setStaticLoading] = useState(false);
  const [staticLoaded, setStaticLoaded] = useState(false);

  // AI exercise state
  const [showAiSession, setShowAiSession] = useState(false);
  const [exercises, setExercises] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [fillAnswer, setFillAnswer] = useState('');
  const [isCorrect, setIsCorrect] = useState(null);
  const [totalXp, setTotalXp] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [aiLoading, setAiLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [difficulty] = useState('beginner');
  // Filtre state — 'all' veya kategori string'i
  const [activeFilter, setActiveFilter] = useState('all');

  const moduleName = MODULES.find(m => m.id === module);
  const staticEndpoint = STATIC_CONTENT_ENDPOINTS[module] || null;
  const hasStaticEndpoint = Boolean(staticEndpoint);
  const isAnnouncements = module === 'announcements';

  // Fetch static content on mount (only for modules with admin endpoints)
  useEffect(() => {
    if (!hasStaticEndpoint) {
      setStaticLoaded(true);
      return;
    }
    setStaticLoading(true);
    axios.get(`${API}${staticEndpoint}`, { headers: authHeaders() })
      .then(res => {
        // announce-templates → res.data.templates
        // pax-comm/situations → res.data.situations
        const items = res.data.templates || res.data.situations || [];
        setStaticItems(items);
      })
      .catch(() => {
        setStaticItems([]);
      })
      .finally(() => {
        setStaticLoading(false);
        setStaticLoaded(true);
      });
  }, [module, hasStaticEndpoint, staticEndpoint, authHeaders]);

  // Auto-show AI session for modules without admin endpoint
  useEffect(() => {
    if (staticLoaded && !hasStaticEndpoint) {
      generateExercises();
    }
  }, [staticLoaded, hasStaticEndpoint]); // eslint-disable-line react-hooks/exhaustive-deps

  const generateExercises = async (diff) => {
    setAiLoading(true);
    setShowAiSession(true);
    setCompleted(false);
    setCurrentIdx(0);
    setSelectedAnswer(null);
    setFillAnswer('');
    setIsCorrect(null);
    setTotalXp(0);
    setCorrectCount(0);
    try {
      const res = await axios.post(`${API}/cabin-crew/english/generate`, {
        module, language: lang, difficulty: diff || difficulty
      }, { headers: authHeaders() });
      if (res.data.fallback === true) {
        toast.warning(lang === 'tr'
          ? 'AI alıştırma üretemedi, varsayılan içerik gösteriliyor.'
          : 'AI could not generate exercises, showing default content.');
      }
      setExercises(res.data.exercises || []);
    } catch (err) {
      console.error('Error generating exercises:', err);
      const errMsg = getApiErrorMessage(err, lang);
      toast.error(errMsg || (lang === 'tr' ? 'Alıştırmalar yüklenemedi.' : 'Failed to load exercises.'));
    } finally {
      setAiLoading(false);
    }
  };

  const checkAnswer = () => {
    const ex = exercises[currentIdx];
    if (!ex) return;
    const answer = ex.type === 'fill_blank' || ex.type === 'translate' ? fillAnswer : selectedAnswer;
    if (!answer) return;
    const correct = answer.trim().toLowerCase() === (ex.correct_answer || '').trim().toLowerCase();
    setIsCorrect(correct);
    if (correct) {
      setCorrectCount(prev => prev + 1);
      setTotalXp(prev => prev + (ex.xp_value || 10));
    }
  };

  const nextExercise = () => {
    if (currentIdx < exercises.length - 1) {
      setCurrentIdx(prev => prev + 1);
      setSelectedAnswer(null);
      setFillAnswer('');
      setIsCorrect(null);
    } else {
      setCompleted(true);
    }
  };

  const currentEx = exercises[currentIdx];

  // --- Loading: static content or no-endpoint AI init ---
  if (staticLoading || (!staticLoaded)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-app)]">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-[var(--brand-primary)] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="font-bold text-sm">{t('cabinCrew.aviationEnglish.loadingStatic')}</p>
        </div>
      </div>
    );
  }

  // --- AI Session loading ---
  if (showAiSession && aiLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-app)]">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-[var(--brand-primary)] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="font-bold text-sm">{lang === 'tr' ? 'Dersler hazirlaniyor...' : 'Preparing exercises...'}</p>
        </div>
      </div>
    );
  }

  // --- AI Session completed ---
  if (showAiSession && completed) {
    return (
      <div className="min-h-screen bg-[var(--bg-app)] flex items-center justify-center" data-testid="english-complete">
        <div className="card-clay p-8 max-w-md text-center">
          <Award size={48} className="mx-auto text-[var(--brand-secondary)] mb-4" />
          <h2 className="font-display text-2xl font-bold mb-2">
            {correctCount === exercises.length
              ? (lang === 'tr' ? 'Mükemmel!' : 'Perfect!')
              : (lang === 'tr' ? 'Tamamlandı!' : 'Completed!')}
          </h2>
          <p className="text-[var(--text-muted)] mb-4">{correctCount}/{exercises.length} {lang === 'tr' ? 'doğru' : 'correct'}</p>
          <div className="inline-flex items-center gap-2 bg-[#FFF4CC] px-6 py-3 rounded-xl mb-6">
            <Zap size={20} className="text-[var(--brand-secondary-shadow)]" />
            <span className="font-bold text-lg">+{totalXp} XP</span>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => generateExercises()}
              className="btn-primary px-6 py-2.5 flex-1 flex items-center justify-center gap-1"
            >
              <RefreshCw size={16} />{lang === 'tr' ? 'Tekrar' : 'Retry'}
            </button>
            {hasStaticEndpoint ? (
              <button
                onClick={() => { setShowAiSession(false); setCompleted(false); }}
                className="btn-tactile px-6 py-2.5 flex-1 bg-white border-[var(--border-light)]"
              >
                {t('cabinCrew.aviationEnglish.backToTemplates')}
              </button>
            ) : (
              <button
                onClick={onBack}
                className="btn-tactile px-6 py-2.5 flex-1 bg-white border-[var(--border-light)]"
              >
                {lang === 'tr' ? 'Geri Don' : 'Go Back'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // --- AI Session active (exercises) ---
  if (showAiSession && exercises.length > 0) {
    return (
      <div className="min-h-screen bg-[var(--bg-app)]" data-testid="aviation-english-session">
        {/* Top bar */}
        <div className="bg-white border-b-2 border-[var(--border-light)] px-4 py-3 sticky top-16 z-30">
          <div className="max-w-3xl mx-auto flex items-center gap-4">
            <button
              onClick={() => hasStaticEndpoint ? setShowAiSession(false) : onBack()}
              className="text-[var(--text-muted)]"
              aria-label={lang === 'tr' ? 'Geri' : 'Back'}
            >
              <ChevronLeft size={24} />
            </button>
            <Progress value={((currentIdx + (isCorrect !== null ? 1 : 0)) / exercises.length) * 100} className="h-3 flex-1" />
            <span className="text-sm font-bold">{currentIdx + 1}/{exercises.length}</span>
            <div className="flex items-center gap-1">
              <Zap size={14} className="text-[var(--brand-secondary)]" />
              <span className="text-sm font-bold">{totalXp}</span>
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 py-8">
          {currentEx && (
            <AnimatePresence mode="wait">
              <motion.div key={currentIdx} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}>
                {/* Exercise type badge */}
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[10px] font-bold bg-sky-100 text-sky-700 px-2 py-0.5 rounded-lg capitalize">
                    {currentEx.context || module}
                  </span>
                  <span className="text-[10px] font-bold bg-[#FFF4CC] text-[var(--brand-secondary-shadow)] px-2 py-0.5 rounded-lg">
                    +{currentEx.xp_value || 10} XP
                  </span>
                </div>

                {/* Instruction */}
                <p className="text-sm font-bold text-[var(--text-muted)] mb-2">{currentEx.instruction}</p>

                {/* English text highlight */}
                {currentEx.english_text && currentEx.type !== 'fill_blank' && (
                  <div className="card-clay p-4 mb-4 bg-sky-50 border-sky-200">
                    <div className="flex items-center gap-2">
                      <Globe size={16} className="text-sky-600" />
                      <p className="font-bold text-sky-800">{currentEx.english_text}</p>
                    </div>
                    {currentEx.native_text && (
                      <p className="text-xs text-[var(--text-muted)] mt-1 ml-6">{currentEx.native_text}</p>
                    )}
                  </div>
                )}

                {/* Question */}
                <div className="card-clay p-6 mb-4">
                  <h3 className="font-display text-lg font-bold mb-4">{currentEx.question}</h3>

                  {/* Multiple choice */}
                  {(currentEx.type === 'multiple_choice' || currentEx.type === 'translate') && currentEx.options?.length > 0 && (
                    <div className="space-y-2">
                      {currentEx.options.map((opt, oi) => {
                        let cls = 'quiz-option';
                        if (isCorrect !== null) {
                          if (opt.toLowerCase() === currentEx.correct_answer?.toLowerCase()) cls += ' correct';
                          else if (opt === selectedAnswer && !isCorrect) cls += ' incorrect';
                        } else if (opt === selectedAnswer) cls += ' selected';
                        return (
                          <button
                            key={oi}
                            onClick={() => isCorrect === null && setSelectedAnswer(opt)}
                            className={`w-full text-left ${cls}`}
                            disabled={isCorrect !== null}
                            data-testid={`eng-option-${oi}`}
                          >
                            <span className="mr-3 inline-flex items-center justify-center w-7 h-7 rounded-lg border-2 border-current text-xs font-bold">
                              {String.fromCharCode(65 + oi)}
                            </span>
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Fill blank / listen repeat */}
                  {(currentEx.type === 'fill_blank' || currentEx.type === 'listen_repeat' || currentEx.type === 'dialogue' || (currentEx.type === 'translate' && (!currentEx.options || currentEx.options.length === 0))) && (
                    <input
                      type="text"
                      value={fillAnswer}
                      onChange={e => setFillAnswer(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && isCorrect === null && fillAnswer && checkAnswer()}
                      disabled={isCorrect !== null}
                      placeholder={lang === 'tr' ? 'Cevabinizi yazin...' : 'Type your answer...'}
                      className={`w-full px-4 py-3 rounded-xl border-2 text-center font-bold focus:outline-none ${isCorrect === null ? 'border-[var(--border-light)] focus:border-[var(--brand-primary)]' : isCorrect ? 'border-[var(--brand-success)] bg-green-50' : 'border-[var(--brand-danger)] bg-red-50'}`}
                      data-testid="eng-fill-input"
                    />
                  )}
                </div>

                {/* Result feedback */}
                {isCorrect !== null && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`card-clay p-4 mb-4 ${isCorrect ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
                  >
                    <p className="font-bold flex items-center gap-2">
                      {isCorrect
                        ? <CheckCircle size={18} className="text-green-500" />
                        : <X size={18} className="text-red-500" />}
                      {isCorrect
                        ? (lang === 'tr' ? 'Doğru!' : 'Correct!')
                        : (lang === 'tr' ? 'Yanlış!' : 'Incorrect!')}
                    </p>
                    {!isCorrect && (
                      <p className="text-xs font-medium mt-1 ml-7">
                        {lang === 'tr' ? 'Doğru cevap: ' : 'Correct: '}<strong>{currentEx.correct_answer}</strong>
                      </p>
                    )}
                    <p className="text-xs text-[var(--text-muted)] mt-1 ml-7">{currentEx.explanation}</p>
                  </motion.div>
                )}

                {/* Action */}
                <div className="flex justify-end">
                  {isCorrect === null ? (
                    <button
                      onClick={checkAnswer}
                      disabled={(currentEx.type === 'multiple_choice' || currentEx.type === 'translate') && currentEx.options?.length > 0 ? !selectedAnswer : !fillAnswer}
                      className="btn-primary px-8 py-3 disabled:opacity-50"
                      data-testid="eng-check-btn"
                    >
                      {lang === 'tr' ? 'Kontrol Et' : 'Check'}
                    </button>
                  ) : (
                    <button onClick={nextExercise} className="btn-success px-8 py-3" data-testid="eng-next-btn">
                      {currentIdx < exercises.length - 1
                        ? (lang === 'tr' ? 'Devam' : 'Continue')
                        : (lang === 'tr' ? 'Bitir' : 'Finish')}
                    </button>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </div>
    );
  }

  // --- Static content view (default for announcements / passenger_communication) ---
  const headingKey = isAnnouncements
    ? 'cabinCrew.aviationEnglish.staticContentHeading'
    : 'cabinCrew.aviationEnglish.staticContentHeadingPax';

  // Kategori listesini dinamik olarak static items'dan çıkar
  const allCategories = [...new Set(staticItems.map(item => item.category).filter(Boolean))];

  // Kategori label'ı i18n'den çek; bilinmiyorsa olduğu gibi göster
  const getCategoryLabel = (cat) => {
    const key = `cabinCrew.categories.${cat?.toLowerCase()}`;
    const label = t(key);
    // t() key bulamazsa key'i döner; bu durumda raw cat kullan
    return (label && label !== key) ? label : cat;
  };

  // Filtrelenmiş item listesi
  const filteredItems = activeFilter === 'all'
    ? staticItems
    : staticItems.filter(item => item.category === activeFilter);

  // Kategori bazlı sayımlar
  const categoryCounts = allCategories.reduce((acc, cat) => {
    acc[cat] = staticItems.filter(item => item.category === cat).length;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[var(--bg-app)]" data-testid="aviation-english-static">
      {/* Sticky header — Geri butonu + modül adı */}
      <div className="sticky top-16 z-30 bg-white/95 backdrop-blur-sm border-b-2 border-[var(--border-light)]">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-sm font-bold text-gray-700 hover:text-gray-900 transition-colors"
            aria-label={t('common.back')}
          >
            <ArrowLeft size={18} />
            {t('common.back')}
          </button>
          <h1 className="font-display font-bold text-lg leading-tight text-gray-800">
            {lang === 'tr' ? (moduleName?.label_tr || module) : (moduleName?.label || module)}
          </h1>
          {/* Spacer — başlığı ortala */}
          <div className="w-16" />
        </div>

        {/* Filtre çubuğu — kategori varsa göster */}
        {allCategories.length > 0 && (
          <div className="max-w-3xl mx-auto px-4 pb-3">
            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide" role="tablist" aria-label={t('cabinCrew.filterBy')}>
              <button
                role="tab"
                aria-selected={activeFilter === 'all'}
                onClick={() => setActiveFilter('all')}
                className={cn(
                  'px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap flex-shrink-0 transition-all duration-150',
                  activeFilter === 'all'
                    ? 'bg-emerald-500 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                )}
                data-testid="filter-all"
              >
                {t('common.all')} ({staticItems.length})
              </button>
              {allCategories.map(cat => (
                <button
                  key={cat}
                  role="tab"
                  aria-selected={activeFilter === cat}
                  onClick={() => setActiveFilter(cat)}
                  className={cn(
                    'px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap flex-shrink-0 transition-all duration-150',
                    activeFilter === cat
                      ? 'bg-emerald-500 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  )}
                  data-testid={`filter-${cat}`}
                >
                  {getCategoryLabel(cat)} ({categoryCounts[cat] || 0})
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6">
        {staticItems.length > 0 ? (
          <>
            {/* Grid layout — 1/2/3 kolon */}
            <div
              className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 items-stretch"
              role="list"
              aria-label={t(headingKey)}
            >
              {filteredItems.length > 0 ? (
                filteredItems.map((item, idx) => (
                  <div key={item.id || idx} role="listitem" className="flex">
                    {isAnnouncements
                      ? <AnnounceTemplateCard item={item} lang={lang} t={t} getField={getField} />
                      : <PaxCommSituationCard item={item} lang={lang} t={t} getField={getField} />}
                  </div>
                ))
              ) : (
                <div className="col-span-full text-center py-10 text-[var(--text-muted)] text-sm font-medium">
                  {t('cabinCrew.aviationEnglish.noStaticContent')}
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-px bg-[var(--border-light)]" />
              <span className="text-xs font-bold text-[var(--text-muted)] uppercase">
                {t('cabinCrew.aviationEnglish.orDivider')}
              </span>
              <div className="flex-1 h-px bg-[var(--border-light)]" />
            </div>
          </>
        ) : (
          <div className="text-center py-10 text-[var(--text-muted)] text-sm font-medium mb-4">
            {t('cabinCrew.aviationEnglish.noStaticContent')}
          </div>
        )}

        {/* AI generate button */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => generateExercises()}
          className="btn-primary w-full py-4 text-base font-bold flex items-center justify-center gap-2"
          data-testid="generate-ai-btn"
          aria-label={t('cabinCrew.aviationEnglish.generateAi')}
        >
          <Zap size={20} />
          {t('cabinCrew.aviationEnglish.generateAi')}
        </motion.button>
      </div>
    </div>
  );
}


// ============ VOICE EXERCISES DATA ============
const VOICE_EXERCISES = [
  {
    id: 'welcome', category: 'Hoş Geldiniz Anonsu', category_en: 'Welcome Announcement',
    icon: '👋', color: 'from-blue-500 to-indigo-600',
    exercises: [
      { id: 'welcome_1', title: 'Temel Karşılama', title_en: 'Basic Greeting', difficulty: 'easy',
        text: "Good morning, ladies and gentlemen. Welcome aboard this flight. My name is [Name], and I will be your cabin crew member today. We hope you have a pleasant flight.",
        text_tr: "Günaydın, bayanlar ve baylar. Bu uçuşa hoş geldiniz. Benim adım [İsim], bugün kabin ekibiniz olacağım. Keyifli bir uçuş geçirmenizi dileriz.",
        tips: ["Speak clearly and slowly", "Smile while speaking", "Maintain a warm tone"] },
      { id: 'welcome_2', title: 'Akşamüstü Karşılama', title_en: 'Evening Greeting', difficulty: 'easy',
        text: "Good evening, ladies and gentlemen. On behalf of the captain and the entire crew, I would like to welcome you on board. Please take your seats and fasten your seatbelts.",
        text_tr: "İyi akşamlar, bayanlar ve baylar. Kaptan ve tüm ekip adına, uçağımıza hoş geldiniz demek istiyorum. Lütfen koltuklarınıza oturun ve emniyet kemerlerinizi bağlayın.",
        tips: ["Use formal tone", "Project confidence", "Emphasize key instructions"] },
      { id: 'welcome_3', title: 'Özel Karşılama', title_en: 'Special Welcome', difficulty: 'medium',
        text: "Good morning and welcome aboard Flight TK1234 to Istanbul. My name is [Name], and together with my colleagues, we will be taking care of you during this flight. Our estimated flight time is approximately 3 hours and 15 minutes.",
        text_tr: "Günaydın ve İstanbul seferimiz TK1234'e hoş geldiniz. Benim adım [İsim], meslektaşlarımla birlikte bu uçuş boyunca sizinle ilgileneceğiz. Tahmini uçuş süremiz yaklaşık 3 saat 15 dakikadır.",
        tips: ["Include flight number naturally", "State time clearly", "Sound welcoming"] },
      { id: 'welcome_4', title: 'Aktarma Bilgisi', title_en: 'Connection Info', difficulty: 'medium',
        text: "For passengers connecting to other flights, please check the monitors in the transit area for your gate information. Our ground staff will be happy to assist you.",
        text_tr: "Diğer uçuşlara aktarma yapacak yolcularımız, lütfen transit alandaki monitörlerden kapı bilgilerinizi kontrol edin. Yer personelimiz size yardımcı olmaktan mutluluk duyacaktır.",
        tips: ["Speak at moderate pace", "Emphasize 'transit area' and 'gate'"] },
      { id: 'welcome_5', title: 'VIP Karşılama', title_en: 'Business Class Welcome', difficulty: 'hard',
        text: "Welcome aboard our business class cabin. We have prepared a selection of premium beverages and a gourmet menu for your enjoyment. Please do not hesitate to press your call button should you require anything at all during the flight.",
        text_tr: "Business class kabinimize hoş geldiniz. Keyfiniz için premium içecekler ve gurme menü hazırladık. Uçuş boyunca herhangi bir ihtiyacınız olursa lütfen çağrı butonunuza basmaktan çekinmeyin.",
        tips: ["Extra polite and refined tone", "Slower pace", "Emphasize luxury"] }
    ]
  },
  {
    id: 'safety', category: 'Güvenlik Anonsu', category_en: 'Safety Announcement',
    icon: '🔒', color: 'from-green-500 to-emerald-600',
    exercises: [
      { id: 'safety_1', title: 'Emniyet Kemeri', title_en: 'Seat Belt', difficulty: 'easy',
        text: "Ladies and gentlemen, the captain has turned on the fasten seatbelt sign. Please return to your seats and make sure your seatbelt is securely fastened.",
        text_tr: "Bayanlar ve baylar, kaptan emniyet kemeri işaretini yakmıştır. Lütfen koltuklarınıza dönün ve emniyet kemerinizin güvenli bir şekilde bağlandığından emin olun.",
        tips: ["Firm but calm tone", "Clear pronunciation of 'seatbelt'"] },
      { id: 'safety_2', title: 'Elektronik Cihazlar', title_en: 'Electronic Devices', difficulty: 'easy',
        text: "Please switch all electronic devices to airplane mode or turn them off completely. The use of personal electronic devices is permitted during the flight with airplane mode activated.",
        text_tr: "Lütfen tüm elektronik cihazlarınızı uçak moduna alın veya tamamen kapatın. Uçuş esnasında uçak modu aktif olarak kişisel elektronik cihaz kullanımı serbesttir.",
        tips: ["Be clear about airplane mode", "Friendly reminder tone"] },
      { id: 'safety_3', title: 'Çıkış Kapılar', title_en: 'Emergency Exits', difficulty: 'medium',
        text: "There are six emergency exits on this aircraft. Two at the front, two over the wings, and two at the rear. Please take a moment to locate the exit nearest to you, keeping in mind that it may be behind you.",
        text_tr: "Bu uçakta altı adet acil çıkış kapısı bulunmaktadır. İkisi önde, ikisi kanatların üzerinde ve ikisi arkada. Lütfen size en yakın çıkışın yerini belirleyin, arkanızda olabileceğini aklınızda tutun.",
        tips: ["Point to exits as you speak", "Pause between locations", "Emphasize 'behind you'"] },
      { id: 'safety_4', title: 'Oksijen Maskesi', title_en: 'Oxygen Mask', difficulty: 'medium',
        text: "In the unlikely event of a loss of cabin pressure, oxygen masks will drop from the panel above your head. Pull the mask towards you, place it over your nose and mouth, and breathe normally. Please secure your own mask before assisting others.",
        text_tr: "Kabin basıncında bir kayıp olması halinde, oksijen maskeleri başınızın üzerindeki panelden düşecektir. Maskeyi kendinize doğru çekin, burnunuz ve ağzınızın üzerine yerleştirin ve normal nefes alın. Lütfen başkalarına yardım etmeden önce kendi maskenizi takın.",
        tips: ["Calm and reassuring", "Demonstrate actions", "Emphasize 'before assisting others'"] },
      { id: 'safety_5', title: 'Can Yeleği', title_en: 'Life Vest', difficulty: 'hard',
        text: "A life vest is located under your seat or between the armrests. In the event of an emergency landing on water, remove the vest from its pouch, place it over your head, clip the strap across your chest, and pull the straps to tighten. Inflate the vest only when you reach the exit by pulling the red tabs. If the vest does not inflate, use the manual inflation tubes.",
        text_tr: "Can yeleği koltuğunuzun altında veya kol dayanakları arasında bulunmaktadır. Suya acil iniş yapılması durumunda, yeleği torbasından çıkarın, başınızın üzerinden geçirin, kayışı göğsünüzden geçirerek klipsi takın ve sıkılaştırmak için kayışları çekin. Yeleği sadece çıkışa ulaştığınızda kırmızı tırtıkları çekerek şişirin.",
        tips: ["Step by step, pause between each", "Very clear enunciation", "Demonstrate physically"] }
    ]
  },
  {
    id: 'turbulence', category: 'Türbülans Anonsu', category_en: 'Turbulence Announcement',
    icon: '💨', color: 'from-amber-500 to-orange-600',
    exercises: [
      { id: 'turb_1', title: 'Hafif Türbülans', title_en: 'Light Turbulence', difficulty: 'easy',
        text: "Ladies and gentlemen, we are experiencing some light turbulence. Please remain seated with your seatbelt fastened. There is nothing to worry about.",
        text_tr: "Bayanlar ve baylar, hafif bir türbülansla karşılaşıyoruz. Lütfen emniyet kemeriniz bağlı olarak koltuğunuzda kalın. Endişelenecek bir durum yoktur.",
        tips: ["Calm, reassuring voice", "Don't rush"] },
      { id: 'turb_2', title: 'Orta Türbülans', title_en: 'Moderate Turbulence', difficulty: 'medium',
        text: "Ladies and gentlemen, the captain has informed us that we will be passing through an area of moderate turbulence. For your safety, please remain in your seats with your seatbelts securely fastened. Cabin service will be temporarily suspended.",
        text_tr: "Bayanlar ve baylar, kaptan bizi orta şiddetli bir türbülans bölgesinden geçeceğimiz konusunda bilgilendirdi. Güvenliğiniz için lütfen emniyet kemeriniz bağlı olarak koltuğunuzda kalın. Kabin servisi geçici olarak askıya alınmıştır.",
        tips: ["Confident and calm", "Emphasize 'for your safety'", "Clear about service suspension"] },
      { id: 'turb_3', title: 'Şiddetli Türbülans', title_en: 'Severe Turbulence', difficulty: 'hard',
        text: "Attention please. The captain has turned on the fasten seatbelt sign due to expected severe turbulence ahead. All passengers must return to their seats immediately and ensure seatbelts are tightly fastened. Cabin crew, please secure the cabin and take your seats.",
        text_tr: "Dikkat lütfen. Kaptan, önümüzdeki şiddetli türbülans nedeniyle emniyet kemeri işaretini yakmıştır. Tüm yolcuların derhal koltuklarına dönmesi ve emniyet kemerlerini sıkıca bağlamaları gerekmektedir. Kabin ekibi, lütfen kabini emniyete alın ve yerlerinize oturun.",
        tips: ["Firm, authoritative tone", "Urgent but not panicked", "Clear commands"] },
      { id: 'turb_4', title: 'Turbulans Sonrasi', title_en: 'Post Turbulence', difficulty: 'easy',
        text: "Ladies and gentlemen, we have now passed through the area of turbulence. The captain has turned off the seatbelt sign. However, we recommend that you keep your seatbelt fastened whenever you are seated. Thank you for your patience.",
        text_tr: "Bayanlar ve baylar, türbülans bölgesini geçmiş bulunuyoruz. Kaptan emniyet kemeri işaretini kapattı. Ancak koltuğunuzda otururken emniyet kemerinizi bağlı tutmanızı öneririz. Sabrınız için teşekkür ederiz.",
        tips: ["Relieved, warm tone", "Thank passengers"] }
    ]
  },
  {
    id: 'landing', category: 'İniş Anonsu', category_en: 'Landing Announcement',
    icon: '✈️', color: 'from-purple-500 to-violet-600',
    exercises: [
      { id: 'land_1', title: 'İnişe Hazırlık', title_en: 'Prepare for Landing', difficulty: 'easy',
        text: "Ladies and gentlemen, we will shortly begin our descent into Istanbul. Please make sure your seatbelt is fastened, your tray table is in the upright position, and your seat back is fully upright.",
        text_tr: "Bayanlar ve baylar, kısa sürede İstanbul'a inişimize başlayacağız. Lütfen emniyet kemerinizin bağlı, tepsi masanızın kapalı ve koltuk arkalığınızın dik konumda olduğundan emin olun.",
        tips: ["Begin calmly", "List each item clearly"] },
      { id: 'land_2', title: 'Yerel Saat Bilgisi', title_en: 'Local Time Info', difficulty: 'easy',
        text: "The local time in Istanbul is 3:45 PM and the temperature is 22 degrees Celsius. We hope you have enjoyed your flight with us today.",
        text_tr: "İstanbul'da yerel saat 15:45 ve sıcaklık 22 derece Celsius'tur. Bugünkü uçuşumuzdan memnun kaldığınızı umuyoruz.",
        tips: ["Clear numbers", "Warm closing tone"] },
      { id: 'land_3', title: 'İniş Sonrası', title_en: 'After Landing', difficulty: 'medium',
        text: "Ladies and gentlemen, welcome to Istanbul Airport. Please remain seated with your seatbelt fastened until the aircraft has come to a complete stop and the seatbelt sign has been turned off. Please be careful when opening the overhead bins as contents may have shifted during the flight.",
        text_tr: "Bayanlar ve baylar, İstanbul Havalimanı'na hoş geldiniz. Uçak tamamen durana ve emniyet kemeri işareti kapanana kadar lütfen emniyet kemeriniz bağlı olarak koltuğunuzda kalın. Üst dolapları açarken lütfen dikkatli olun çünkü içindekiler uçuş sırasında kaymış olabilir.",
        tips: ["Patience emphasis", "Warning about overhead bins", "Professional tone"] },
      { id: 'land_4', title: 'Aktarma Yolcuları', title_en: 'Connecting Passengers', difficulty: 'medium',
        text: "Passengers with connecting flights, please proceed to the transfer desk in the transit area where our ground staff will assist you with your next flight details. Please have your boarding pass ready.",
        text_tr: "Aktarma uçuşu olan yolcularımız, lütfen transit alandaki transfer masasına gidin, yer personelimiz bir sonraki uçuş detaylarınız konusunda size yardımcı olacaktır. Lütfen biniş kartınızı hazır bulundurun.",
        tips: ["Direct passengers clearly", "Mention boarding pass"] },
      { id: 'land_5', title: 'Veda Mesajı', title_en: 'Farewell Message', difficulty: 'easy',
        text: "On behalf of the captain and the entire crew, we thank you for flying with us today. We hope to see you again on board soon. Have a wonderful day and a safe onward journey.",
        text_tr: "Kaptan ve tüm ekip adına, bugün bizimle uçtuğunuz için teşekkür ederiz. Sizi tekrar uçağımızda görmekten mutluluk duyacağız. Harika bir gün ve güvenli bir yolculuk dileriz.",
        tips: ["Genuine warmth", "Smile in your voice", "Perfect closing"] }
    ]
  },
  {
    id: 'service_ann', category: 'Servis Anonsu', category_en: 'Service Announcement',
    icon: '🍽️', color: 'from-teal-500 to-cyan-600',
    exercises: [
      { id: 'serv_1', title: 'Yemek Servisi', title_en: 'Meal Service', difficulty: 'easy',
        text: "Ladies and gentlemen, we will now begin our meal service. Today we have two options: grilled chicken with rice or pasta with tomato sauce. Would you like chicken or pasta?",
        text_tr: "Bayanlar ve baylar, şimdi yemek servisimize başlayacağız. Bugün iki seçeneğimiz var: pirinç ile ızgara tavuk veya domates soslu makarna. Tavuk mu yoksa makarna mı istersiniz?",
        tips: ["Friendly tone", "Clear food options", "Offer choices politely"] },
      { id: 'serv_2', title: 'İçecek Servisi', title_en: 'Beverage Service', difficulty: 'easy',
        text: "Good afternoon. May I offer you something to drink? We have coffee, tea, orange juice, apple juice, water, and soft drinks. What would you prefer?",
        text_tr: "İyi günler. Size içecek ikram edebilir miyim? Kahve, çay, portakal suyu, elma suyu, su ve meşrubatlarımız var. Hangisini tercih edersiniz?",
        tips: ["Warm and attentive", "List beverages at moderate pace"] },
      { id: 'serv_3', title: 'Duty Free Satışı', title_en: 'Duty Free Sales', difficulty: 'medium',
        text: "Ladies and gentlemen, we are now pleased to offer you our duty-free shopping service. Please take a moment to browse through the catalog in your seat pocket. Our crew will be happy to assist you with your purchases. We accept credit cards, euros, and US dollars.",
        text_tr: "Bayanlar ve baylar, şimdi duty-free alışveriş hizmetimizi sunmaktan memnuniyet duyarız. Lütfen koltuk cebinizdeki kataloğu incelemek için bir dakikanızı ayırın. Ekibimiz alışverişlerinizde size yardımcı olmaktan mutluluk duyacaktır. Kredi kartı, euro ve Amerikan doları kabul ediyoruz.",
        tips: ["Enthusiastic but professional", "Mention payment options clearly"] }
    ]
  },
  {
    id: 'delay', category: 'Rotar/Gecikme Anonsu', category_en: 'Delay Announcement',
    icon: '⏰', color: 'from-red-500 to-rose-600',
    exercises: [
      { id: 'delay_1', title: 'Kisa Gecikme', title_en: 'Short Delay', difficulty: 'easy',
        text: "Ladies and gentlemen, we regret to inform you that there will be a slight delay in our departure due to air traffic control. We expect to depart within the next 15 minutes. We apologize for any inconvenience.",
        text_tr: "Bayanlar ve baylar, hava trafik kontrolu nedeniyle kalkisimizda kisa bir gecikme olacagini uzulerek bildiriyoruz. Onumuzdeki 15 dakika icinde kalkis yapmayi bekliyoruz. Olasi rahatsizliktan dolayi ozur dileriz.",
        tips: ["Apologetic tone", "Give time estimate", "Reassure passengers"] },
      { id: 'delay_2', title: 'Uzun Gecikme', title_en: 'Long Delay', difficulty: 'medium',
        text: "Ladies and gentlemen, we apologize for the extended delay. Due to technical maintenance checks, our departure has been postponed. We are working to resolve this as quickly as possible and will keep you updated with any changes. Refreshments will be provided shortly.",
        text_tr: "Bayanlar ve baylar, uzayan gecikme icin ozur dileriz. Teknik bakim kontrolleri nedeniyle kalkisimiz ertelenmistir. Durumu en kisa surede cozmek icin calisiyoruz ve herhangi bir degisiklik konusunda sizi bilgilendirecegiz. Kisa surede ikramlar sunulacaktir.",
        tips: ["Empathetic and professional", "Offer refreshments", "Promise updates"] },
      { id: 'delay_3', title: 'Pist Degisikligi', title_en: 'Gate Change', difficulty: 'medium',
        text: "Attention passengers on Flight TK1234. Please note that the departure gate has been changed from gate B12 to gate C5. We apologize for the inconvenience and ask that you make your way to gate C5 at this time.",
        text_tr: "TK1234 seferinin yolcularina duyuru. Kalkis kapisi B12'den C5'e degistirilmistir. Olasi rahatsizliktan dolayi ozur diler, su anda C5 kapisina gecmenizi rica ederiz.",
        tips: ["Clear gate numbers", "Repeat important info", "Urgent but calm"] }
    ]
  }
];

// ============ VOICE PRACTICE MODULE ============
function VoicePracticeModule({ onBack, onExerciseSaved }) {
  const { lang, t } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();
  const [phase, setPhase] = useState('categories'); // categories, exercises, practice
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedExercise, setSelectedExercise] = useState(null);
  // Override badge: STT dilini kullanici degistirebilir. null = scenario.spoken_language fallback.
  const [langOverride, setLangOverride] = useState(null);
  const [completedExercises, setCompletedExercises] = useState([]);
  const [_recordings, setRecordings] = useState([]);

  // Practice state
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [playingRef, setPlayingRef] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioPlayerRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/cabin-crew/voice-recordings`, { headers: authHeaders() })
      .then(res => {
        const recs = res.data.recordings || [];
        setCompletedExercises(recs.map(r => r.exercise_id));
        setRecordings(recs);
      }).catch(() => {});
  }, [authHeaders]);

  // TTS — referans metni her zaman İngilizce anons; dil tespiti yapılır ama pratik metni en-US
  const playReference = (text) => {
    if (playingRef) { window.speechSynthesis.cancel(); setPlayingRef(false); return; }
    const detectedLang = detectTtsLang(text, 'en'); // referans metin en-US (anons pratiği)
    setPlayingRef(true);
    playTts(text, detectedLang, () => setPlayingRef(false), () => setPlayingRef(false));
  };

  // Record
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];
      // Tarayıcı uyumlu MIME seç
      const mimeType = pickRecorderMime({ audio: true });
      const mr = createMediaRecorder(stream, mimeType);
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      setFeedback(null);
      setAudioBlob(null);
      setAudioUrl(null);
      setSaved(false);
    } catch { toast.error(lang === 'tr' ? 'Mikrofon izni gerekli!' : 'Microphone permission required!'); }
  };

  const stopRecording = () => {
    if (!mediaRecorderRef.current) return;
    mediaRecorderRef.current.onstop = () => {
      const actualMime = mediaRecorderRef.current.mimeType || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type: actualMime });
      setAudioBlob(blob);
      setAudioUrl(URL.createObjectURL(blob));
      mediaRecorderRef.current.stream.getTracks().forEach(t => t.stop());
    };
    mediaRecorderRef.current.stop();
    setRecording(false);
  };

  // Play recording
  const playRecording = () => {
    if (!audioUrl) return;
    if (playing) { audioPlayerRef.current?.pause(); setPlaying(false); return; }
    const audio = new Audio(audioUrl);
    audioPlayerRef.current = audio;
    audio.onended = () => setPlaying(false);
    audio.play();
    setPlaying(true);
  };

  // AI Evaluate
  const evaluateRecording = async () => {
    if (!audioBlob || !selectedExercise) return;
    setEvaluating(true);
    try {
      // Transcribe — dil onceligi: override > selectedExercise.spoken_language > 'en'
      const fd = new FormData();
      fd.append('file', audioBlob, 'recording.webm');
      const exLang = selectedExercise?.spoken_language || selectedExercise?.spokenLanguage;
      const sttLang = langOverride || exLang || 'en';
      fd.append('language', sttLang);
      const transRes = await axios.post(`${API}/cabin-crew/interview/transcribe`, fd, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      const transcribed = transRes.data.text || '';

      // Evaluate with AI
      const evalRes = await axios.post(`${API}/cabin-crew/voice-practice/evaluate`, {
        reference_text: selectedExercise.text,
        spoken_text: transcribed,
        category: selectedCategory.id
      }, { headers: authHeaders() });

      // Backend returns: { scores: { <criteria>, overall }, feedback_tr, feedback_en,
      //   improvements_tr, improvements_en, strengths_tr, strengths_en, ... }
      // Map to flat shape expected by score grid: pronunciation/clarity/pace/confidence/overall
      const raw = evalRes.data;
      const sc = raw.scores || {};
      // 'general' category criteria: pronunciation, clarity, pace, confidence
      // Other categories: extract matching keys or fall back to first four criteria values
      const criteriaValues = Object.entries(sc).filter(([k]) => k !== 'overall').map(([, v]) => v);
      const normalizedFeedback = {
        pronunciation: sc.pronunciation ?? sc.phrase_accuracy ?? sc.icao_clarity    ?? criteriaValues[0] ?? 0,
        clarity:       sc.clarity       ?? sc.icao_clarity   ?? sc.phrase_accuracy  ?? criteriaValues[1] ?? 0,
        pace:          sc.pace          ?? criteriaValues[2]  ?? 0,
        confidence:    sc.confidence    ?? sc.tone_politeness ?? sc.calm_delivery    ?? criteriaValues[3] ?? 0,
        overall:       sc.overall       ?? 0,
        feedback: lang === 'tr' ? (raw.feedback_tr || '') : (raw.feedback_en || ''),
        tips: lang === 'tr' ? (raw.improvements_tr || []) : (raw.improvements_en || []),
        passed: raw.passed,
        xp_earned: raw.xp_earned ?? 0,
        _raw: raw,
      };
      setFeedback({ ...normalizedFeedback, transcribed_text: transcribed });
    } catch (err) {
      console.error('Eval error:', err);
      const errMsg = getApiErrorMessage(err, lang);
      toast.error(errMsg || (lang === 'tr' ? 'Değerlendirme yapılamadı, lütfen tekrar deneyin.' : 'Evaluation failed, please try again.'));
      setPhase('idle');
    } finally { setEvaluating(false); }
  };

  // Save to server
  const saveRecording = async () => {
    if (!audioBlob || !selectedExercise) return;
    setSaving(true);
    try {
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      reader.onloadend = async () => {
        await axios.post(`${API}/cabin-crew/voice-practice/save`, {
          exercise_id: selectedExercise.id,
          category: selectedCategory.id,
          audio_data: reader.result,
          scores: feedback ? { pronunciation: feedback.pronunciation, clarity: feedback.clarity, pace: feedback.pace, confidence: feedback.confidence, overall: feedback.overall } : {}
        }, { headers: authHeaders() });
        setSaved(true);
        if (!completedExercises.includes(selectedExercise.id)) {
          setCompletedExercises(prev => [...prev, selectedExercise.id]);
          // Notify parent so hero stats update without a full page reload
          if (typeof onExerciseSaved === 'function') {
            onExerciseSaved(selectedExercise.id);
          }
        }
        setSaving(false);
      };
    } catch { setSaving(false); }
  };

  const resetPractice = () => {
    setAudioBlob(null); setAudioUrl(null); setFeedback(null); setSaved(false);
    if (audioPlayerRef.current) audioPlayerRef.current.pause();
  };

  const handleNextExercise = () => {
    if (!selectedCategory || !selectedExercise) return;
    const exercises = selectedCategory.exercises;
    const currentIndex = exercises.findIndex(e => e.id === selectedExercise.id);
    const nextExercise = exercises[currentIndex + 1];
    if (nextExercise) {
      setSelectedExercise(nextExercise);
      resetPractice();
    } else {
      // Son egzersiz — listeye dön
      toast.success(t('cabinCrew.voice.allDone'));
      setSelectedExercise(null);
      setPhase('exercises');
      resetPractice();
    }
  };

  const isLastExercise = () => {
    if (!selectedCategory || !selectedExercise) return false;
    const exercises = selectedCategory.exercises;
    const currentIndex = exercises.findIndex(e => e.id === selectedExercise.id);
    return currentIndex === exercises.length - 1;
  };

  const scoreColor = (score) => score >= 85 ? 'text-green-600' : score >= 70 ? 'text-yellow-600' : 'text-red-500';
  const scoreBg = (score) => score >= 85 ? 'bg-green-50 border-green-200' : score >= 70 ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200';

  // === CATEGORIES SCREEN ===
  if (phase === 'categories') {
    return (
      <div className="min-h-screen bg-[var(--bg-app)]" data-testid="voice-practice-categories">
        <div className="bg-gradient-to-br from-pink-500 to-rose-600 text-white py-10">
          <div className="max-w-4xl mx-auto px-4">
            <button onClick={onBack} className="text-white/70 hover:text-white mb-3 flex items-center gap-1 text-sm font-bold">
              <ChevronLeft size={18} /> {lang === 'tr' ? 'Geri Don' : 'Go Back'}
            </button>
            <h1 className="font-display text-3xl font-bold flex items-center gap-2">
              <Headphones size={28} /> {lang === 'tr' ? 'İnteraktif Eğitimler' : 'Interactive Training'}
            </h1>
            <p className="text-pink-200 mt-1 font-medium">{lang === 'tr' ? 'Sesli anons ve konusma pratigi yapin' : 'Practice voice announcements and speaking'}</p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* How it works */}
          <div className="card-clay p-5 mb-6 bg-pink-50 border-pink-200">
            <h3 className="font-bold text-sm mb-2">{lang === 'tr' ? 'Nasıl Çalışır?' : 'How it Works?'}</h3>
            <div className="grid grid-cols-4 gap-3 text-center text-xs font-semibold text-[var(--text-muted)]">
              <div><div className="w-10 h-10 rounded-full bg-white mx-auto mb-1 flex items-center justify-center text-lg">1</div>{lang === 'tr' ? 'Metni oku' : 'Read text'}</div>
              <div><div className="w-10 h-10 rounded-full bg-white mx-auto mb-1 flex items-center justify-center text-lg">2</div>{lang === 'tr' ? 'Dinle' : 'Listen'}</div>
              <div><div className="w-10 h-10 rounded-full bg-white mx-auto mb-1 flex items-center justify-center text-lg">3</div>{lang === 'tr' ? 'Kaydet' : 'Record'}</div>
              <div><div className="w-10 h-10 rounded-full bg-white mx-auto mb-1 flex items-center justify-center text-lg">4</div>{lang === 'tr' ? 'Değerlendir' : 'Evaluate'}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {VOICE_EXERCISES.map((cat, i) => {
              const completed = cat.exercises.filter(e => completedExercises.includes(e.id)).length;
              return (
                <motion.button key={cat.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  onClick={() => { setSelectedCategory(cat); setPhase('exercises'); }}
                  className="card-clay p-5 text-left hover:border-pink-400 transition-all group" data-testid={`vp-cat-${cat.id}`}>
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${cat.color} flex items-center justify-center text-2xl mb-3`}>{cat.icon}</div>
                  <h3 className="font-bold text-sm mb-0.5">{lang === 'tr' ? cat.category : cat.category_en}</h3>
                  <p className="text-[10px] text-[var(--text-muted)]">{cat.exercises.length} {lang === 'tr' ? 'egzersiz' : 'exercises'}</p>
                  {completed > 0 && (
                    <div className="mt-2 flex items-center gap-1">
                      <div className="h-1.5 flex-1 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-green-500 rounded-full" style={{ width: `${(completed / cat.exercises.length) * 100}%` }} /></div>
                      <span className="text-[9px] font-bold text-green-600">{completed}/{cat.exercises.length}</span>
                    </div>
                  )}
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // === EXERCISES LIST ===
  if (phase === 'exercises' && selectedCategory) {
    return (
      <div className="min-h-screen bg-[var(--bg-app)]" data-testid="voice-practice-exercises">
        <div className={`bg-gradient-to-br ${selectedCategory.color} text-white py-8`}>
          <div className="max-w-4xl mx-auto px-4">
            <button onClick={() => setPhase('categories')} className="text-white/70 hover:text-white mb-2 flex items-center gap-1 text-sm font-bold">
              <ChevronLeft size={18} /> {lang === 'tr' ? 'Kategoriler' : 'Categories'}
            </button>
            <h1 className="font-display text-2xl font-bold flex items-center gap-2">
              <span className="text-3xl">{selectedCategory.icon}</span> {lang === 'tr' ? selectedCategory.category : selectedCategory.category_en}
            </h1>
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-4 py-6 space-y-3">
          {selectedCategory.exercises.map((ex, i) => {
            const done = completedExercises.includes(ex.id);
            return (
              <motion.button key={ex.id} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                onClick={() => { setSelectedExercise(ex); setPhase('practice'); resetPractice(); }}
                className={`card-clay p-4 w-full text-left flex items-center gap-4 hover:border-pink-400 transition-all ${done ? 'border-green-200 bg-green-50/50' : ''}`}
                data-testid={`vp-ex-${ex.id}`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${done ? 'bg-green-500' : 'bg-gray-100'}`}>
                  {done ? <CheckCircle size={20} className="text-white" /> : <span className="font-bold text-sm text-[var(--text-muted)]">{i + 1}</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm">{lang === 'tr' ? ex.title : ex.title_en}</p>
                  <p className="text-[11px] text-[var(--text-muted)] truncate">{ex.text.slice(0, 60)}...</p>
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-lg ${ex.difficulty === 'easy' ? 'bg-green-100 text-green-700' : ex.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                  {ex.difficulty === 'easy' ? (lang === 'tr' ? 'Kolay' : 'Easy') : ex.difficulty === 'medium' ? (lang === 'tr' ? 'Orta' : 'Medium') : (lang === 'tr' ? 'Zor' : 'Hard')}
                </span>
                <ChevronRight size={18} className="text-[var(--text-muted)]" />
              </motion.button>
            );
          })}
        </div>
      </div>
    );
  }

  // === PRACTICE SCREEN ===
  if (phase === 'practice' && selectedExercise) {
    return (
      <div className="min-h-screen bg-[var(--bg-app)]" data-testid="voice-practice-screen">
        <div className="bg-white border-b-2 border-[var(--border-light)] px-4 py-3 sticky top-16 z-30">
          <div className="max-w-3xl mx-auto flex items-center gap-3">
            <button onClick={() => { setPhase('exercises'); resetPractice(); }} className="text-[var(--text-muted)]"><ChevronLeft size={24} /></button>
            <div className="flex-1"><p className="font-bold text-sm">{lang === 'tr' ? selectedExercise.title : selectedExercise.title_en}</p>
            <p className="text-[10px] text-[var(--text-muted)]">{lang === 'tr' ? selectedCategory.category : selectedCategory.category_en}</p></div>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-lg ${selectedExercise.difficulty === 'easy' ? 'bg-green-100 text-green-700' : selectedExercise.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
              {selectedExercise.difficulty === 'easy' ? (lang === 'tr' ? 'Kolay' : 'Easy') : selectedExercise.difficulty === 'medium' ? (lang === 'tr' ? 'Orta' : 'Medium') : (lang === 'tr' ? 'Zor' : 'Hard')}
            </span>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
          {/* Reference Text */}
          <div className="card-clay p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-bold text-[var(--text-muted)] uppercase">{lang === 'tr' ? 'Referans Metin' : 'Reference Text'}</h3>
              <button onClick={() => playReference(selectedExercise.text)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 transition-all ${playingRef ? 'bg-pink-500 text-white' : 'bg-pink-50 text-pink-600 hover:bg-pink-100'}`}
                data-testid="play-reference-btn">
                {playingRef ? <Square size={12} /> : <Volume2 size={12} />}
                {playingRef ? (lang === 'tr' ? 'Durdur' : 'Stop') : (lang === 'tr' ? 'Dinle' : 'Listen')}
              </button>
            </div>
            <p className="text-sm font-semibold leading-relaxed text-sky-800 bg-sky-50 p-4 rounded-xl border border-sky-200">{selectedExercise.text}</p>
            <p className="text-xs text-[var(--text-muted)] mt-2 italic">{selectedExercise.text_tr}</p>
          </div>

          {/* Tips */}
          {selectedExercise.tips && (
            <div className="flex flex-wrap gap-2">
              {selectedExercise.tips.map((tip, i) => (
                <span key={i} className="text-[10px] font-bold bg-purple-50 text-purple-600 px-2.5 py-1 rounded-lg">{tip}</span>
              ))}
            </div>
          )}

          {/* Recording Controls */}
          <div className="card-clay p-6 text-center">
            {/* Konusma dili badge — override toggle (TR/EN). Default: selectedExercise.spoken_language */}
            {!feedback && (
              <div className="flex justify-center mb-4">
                <SpokenLangBadge
                  defaultLang={selectedExercise?.spoken_language || selectedExercise?.spokenLanguage || 'en'}
                  override={langOverride}
                  onToggle={setLangOverride}
                  lang={lang}
                />
              </div>
            )}
            {!recording && !audioBlob && (
              <div>
                <button onClick={startRecording}
                  className="w-24 h-24 rounded-full bg-gradient-to-br from-pink-500 to-rose-600 text-white flex items-center justify-center mx-auto shadow-xl hover:shadow-2xl transition-all hover:scale-105"
                  data-testid="vp-record-btn">
                  <Mic size={36} />
                </button>
                <p className="text-sm font-bold mt-3 text-[var(--text-muted)]">{lang === 'tr' ? 'Kayda baslamak icin tiklayin' : 'Click to start recording'}</p>
              </div>
            )}

            {recording && (
              <div>
                <button onClick={stopRecording}
                  className="w-24 h-24 rounded-full bg-red-600 text-white flex items-center justify-center mx-auto shadow-xl animate-pulse"
                  data-testid="vp-stop-btn">
                  <Square size={28} fill="white" />
                </button>
                <p className="text-sm font-bold mt-3 text-red-500">{lang === 'tr' ? 'Kayıt yapılıyor... Durdurmak için tıklayın' : 'Recording... Click to stop'}</p>
              </div>
            )}

            {audioBlob && !recording && (
              <div className="space-y-4">
                <div className="flex justify-center gap-3">
                  <button onClick={playRecording}
                    className={`px-5 py-2.5 rounded-xl font-bold text-sm flex items-center gap-2 transition-all ${playing ? 'bg-pink-500 text-white' : 'bg-gray-100 text-[var(--text-muted)] hover:bg-gray-200'}`}
                    data-testid="vp-play-btn">
                    {playing ? <Square size={14} /> : <Play size={14} />}
                    {playing ? (lang === 'tr' ? 'Durdur' : 'Stop') : (lang === 'tr' ? 'Kaydi Dinle' : 'Play Recording')}
                  </button>
                  <button onClick={resetPractice}
                    className="px-5 py-2.5 rounded-xl font-bold text-sm bg-gray-100 text-[var(--text-muted)] hover:bg-gray-200 flex items-center gap-2">
                    <RefreshCw size={14} /> {lang === 'tr' ? 'Tekrar Kaydet' : 'Re-record'}
                  </button>
                </div>

                {/* Evaluate */}
                {!feedback && (
                  <button onClick={evaluateRecording} disabled={evaluating}
                    className="btn-primary px-8 py-3 mx-auto disabled:opacity-50" data-testid="vp-evaluate-btn">
                    {evaluating ? (
                      <span className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> {lang === 'tr' ? 'AI Degerlendirilyor...' : 'AI Evaluating...'}</span>
                    ) : (
                      <span className="flex items-center gap-2"><Zap size={16} /> {lang === 'tr' ? 'AI ile Degerlendir' : 'Evaluate with AI'}</span>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Feedback */}
          {feedback && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
              {/* Transcription */}
              {feedback.transcribed_text && (
                <div className="card-clay p-4">
                  <p className="text-[10px] font-bold text-[var(--text-muted)] mb-1">{lang === 'tr' ? 'Algilanan Konusma' : 'Detected Speech'}</p>
                  <p className="text-sm font-medium italic">"{feedback.transcribed_text}"</p>
                </div>
              )}

              {/* Scores */}
              <div className="card-clay p-5">
                <div className="grid grid-cols-5 gap-3 text-center">
                  {[
                    { key: 'pronunciation', label: lang === 'tr' ? 'Telaffuz' : 'Pronunciation' },
                    { key: 'clarity', label: lang === 'tr' ? 'Netlik' : 'Clarity' },
                    { key: 'pace', label: lang === 'tr' ? 'Hiz' : 'Pace' },
                    { key: 'confidence', label: lang === 'tr' ? 'Guven' : 'Confidence' },
                    { key: 'overall', label: lang === 'tr' ? 'Genel' : 'Overall' }
                  ].map(s => (
                    <div key={s.key} className={`p-3 rounded-xl border ${scoreBg(feedback[s.key] || 0)}`}>
                      <p className={`font-display text-2xl font-bold ${scoreColor(feedback[s.key] || 0)}`}>{feedback[s.key] || 0}</p>
                      <p className="text-[9px] font-bold text-[var(--text-muted)]">{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* AI Feedback */}
              {feedback.feedback && (
                <div className="card-clay p-4 bg-sky-50 border-sky-200">
                  <p className="text-xs font-bold text-sky-700 mb-1">{lang === 'tr' ? 'AI Geri Bildirim' : 'AI Feedback'}</p>
                  <p className="text-sm font-medium text-sky-800">{feedback.feedback}</p>
                </div>
              )}

              {/* AI Tips */}
              {feedback.tips && feedback.tips.length > 0 && (
                <div className="card-clay p-4">
                  <p className="text-xs font-bold text-[var(--text-muted)] mb-2">{lang === 'tr' ? 'Geliştirme Önerileri' : 'Improvement Tips'}</p>
                  {feedback.tips.map((tip, i) => (
                    <p key={i} className="text-xs font-medium flex items-start gap-1 mb-1"><ChevronRight size={12} className="text-pink-500 mt-0.5 flex-shrink-0" />{tip}</p>
                  ))}
                </div>
              )}

              {/* Save */}
              <div className="flex justify-center gap-3 flex-wrap">
                {!saved ? (
                  <button onClick={saveRecording} disabled={saving}
                    className="btn-success px-8 py-3 disabled:opacity-50" data-testid="vp-save-btn">
                    {saving ? '...' : <span className="flex items-center gap-2"><Save size={16} /> {lang === 'tr' ? 'Kaydi Kaydet' : 'Save Recording'}</span>}
                  </button>
                ) : (
                  <div className="flex items-center gap-2 text-green-600 font-bold"><CheckCircle size={18} /> {lang === 'tr' ? 'Kaydedildi!' : 'Saved!'}</div>
                )}
                <button onClick={resetPractice} className="btn-tactile px-6 py-3 bg-white border-[var(--border-light)] flex items-center gap-2">
                  <RefreshCw size={16} /> {t('cabinCrew.voice.tryAgain')}
                </button>
              </div>

              {/* Navigation buttons */}
              <div className="flex gap-3 mt-2" data-testid="vp-nav-buttons">
                {isLastExercise() ? (
                  <button
                    onClick={handleNextExercise}
                    className="flex-1 px-4 py-2.5 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 font-bold flex items-center justify-center gap-2 border-2 border-emerald-600 shadow-[0_3px_0_#059669] active:shadow-none active:translate-y-0.5 transition-all"
                    data-testid="vp-finish-btn"
                  >
                    <Trophy size={16} /> {t('cabinCrew.voice.finishAll')}
                  </button>
                ) : (
                  <button
                    onClick={handleNextExercise}
                    className="flex-1 px-4 py-2.5 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 font-bold flex items-center justify-center gap-2 border-2 border-emerald-600 shadow-[0_3px_0_#059669] active:shadow-none active:translate-y-0.5 transition-all"
                    data-testid="vp-next-btn"
                  >
                    {t('cabinCrew.voice.nextExercise')} <ChevronRight size={16} />
                  </button>
                )}
                <button
                  onClick={() => { setPhase('exercises'); resetPractice(); }}
                  className="px-4 py-2.5 text-gray-600 hover:text-gray-900 rounded-xl hover:bg-gray-100 font-semibold text-sm transition-all"
                  data-testid="vp-close-btn"
                >
                  {t('cabinCrew.voice.close')}
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    );
  }

  return null;
}


// ============ ENGLISH INTERVIEW MODULE (3.7.2) ============
function EnglishInterviewModule({ onBack }) {
  const { lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();

  const [phase, setPhase] = useState('select'); // select, playing
  const [scenarios, setScenarios] = useState([]);
  const [loadingScenarios, setLoadingScenarios] = useState(true);
  const [session, setSession] = useState(null);
  const [starting, setStarting] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/cabin-crew/english-interview/scenarios`, { headers: authHeaders() })
      .then((res) => {
        if (!cancelled) setScenarios(res.data.scenarios || []);
      })
      .catch((err) => {
        if (!cancelled) toast.error(getApiErrorMessage(err, lang));
      })
      .finally(() => {
        if (!cancelled) setLoadingScenarios(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startScenario = async (scenarioId) => {
    setStarting(scenarioId);
    try {
      const res = await axios.post(
        `${API}/cabin-crew/english-interview/start`,
        { scenario: scenarioId },
        { headers: authHeaders() }
      );
      setSession(res.data);
      setPhase('playing');
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setStarting(null);
    }
  };

  const handleAnswer = async (payload) => {
    try {
      const res = await axios.post(
        `${API}/cabin-crew/english-interview/answer`,
        { session_id: session.session_id, ...payload },
        { headers: authHeaders() }
      );

      setSession((prev) => ({
        ...prev,
        current_index: res.data.next_index,
        streak: res.data.current_streak,
        lives: res.data.lives_remaining,
        total_score: res.data.total_score,
        status: res.data.session_status,
        answers: [
          ...(prev.answers || []),
          {
            question_id: payload.question_id,
            answer_text: payload.answer_text,
            answer_type: payload.answer_type,
            evaluation: res.data.evaluation,
            is_correct: res.data.is_correct,
            points_earned: res.data.points_earned,
          },
        ],
      }));

      return res.data;
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
      return null;
    }
  };

  if (phase === 'select') {
    return (
      <div className="min-h-screen bg-[var(--bg-app)] p-4" data-testid="english-interview-select">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={onBack}
            className="mb-4 text-sm text-[var(--text-muted)] hover:text-[var(--text-main)] flex items-center gap-1"
            data-testid="ei-back-btn"
          >
            <ChevronLeft size={16} /> {lang === 'tr' ? 'Geri' : 'Back'}
          </button>
          <div className="flex items-center gap-3 mb-1">
            <Globe size={24} className="text-purple-600" />
            <h2 className="font-display text-2xl font-bold">
              {lang === 'tr' ? 'İngilizce Mülakat' : 'English Interview'}
            </h2>
          </div>
          <p className="text-sm text-[var(--text-muted)] mb-6">
            {lang === 'tr'
              ? 'Senaryo seçin ve Duolingo tarzı bir akışta İngilizce mülakat pratiği yapın.'
              : 'Pick a scenario and practice English interviews in a Duolingo-style flow.'}
          </p>

          {loadingScenarios ? (
            <div className="card-clay p-6 text-center">
              <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : (
            <div className="space-y-3">
              {scenarios.map((s) => {
                const disabled = s.questions_count === 0 || starting !== null;
                return (
                  <button
                    key={s.id}
                    onClick={() => startScenario(s.id)}
                    disabled={disabled}
                    className="w-full p-4 bg-white rounded-2xl border-2 border-gray-200 text-left hover:border-purple-400 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    data-testid={`ei-scenario-${s.id}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-bold">{s[`name_${lang}`] || s.name_en}</div>
                        <div className="text-xs text-[var(--text-muted)]">
                          {s.questions_count}{' '}
                          {lang === 'tr' ? 'soru' : 'questions'} · {s.difficulty}
                        </div>
                      </div>
                      {starting === s.id ? (
                        <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <ChevronRight size={18} className="text-[var(--text-muted)]" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (phase === 'playing' && session) {
    return (
      <div className="min-h-screen bg-[var(--bg-app)] p-4" data-testid="english-interview-playing">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={onBack}
            className="mb-4 text-sm text-[var(--text-muted)] hover:text-[var(--text-main)] flex items-center gap-1"
            data-testid="ei-cancel-btn"
          >
            <ChevronLeft size={16} /> {lang === 'tr' ? 'İptal' : 'Cancel'}
          </button>
          <EnglishInterviewPlayer
            session={session}
            onAnswer={handleAnswer}
            onComplete={onBack}
            lang={lang}
          />
        </div>
      </div>
    );
  }

  return null;
}

